/* Detail drawers for quotes (la NP es una cotización más) */

function Drawer({ onClose, title, subtitle, headerExtras, children, footer, width=900 }) {
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 modal-overlay" onClick={onClose}/>
      <div className="bg-white shadow-pop modal-enter flex flex-col" style={{ width }}>
        <div className="px-6 py-4 border-b border-line flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="page-head-sub">{subtitle}</div>
            <h3 className="text-[17px] font-bold text-ink-900 mt-0.5 truncate" style={{letterSpacing: '-0.015em'}}>{title}</h3>
          </div>
          <div className="flex items-center gap-2">
            {headerExtras}
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-surface flex items-center justify-center text-ink-400 hover:text-ink-700 transition-colors"><Icon name="x" size={16}/></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin">{children}</div>
        {footer && <div className="px-6 py-3 border-t border-line bg-surface/80 flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

function StagePipeline({ stages, currentId }) {
  const currentIdx = stages.findIndex(s => s.id === currentId);
  return (
    <div className="flex items-stretch w-full rounded-lg overflow-hidden border border-line">
      {stages.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        const tone = s.tone;
        return (
          <div key={s.id} className={cx(
            'flex-1 px-3 py-2 flex items-center gap-2 border-r border-line last:border-r-0 text-[11.5px]',
            active ? 'bg-white' : done ? 'bg-surface' : 'bg-white/60'
          )}>
            <span className={cx('w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold',
              active ? 'text-white' : done ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-300/40 text-ink-500'
            )} style={active ? {background: STAGE_DOT[tone]} : undefined}>
              {done ? '✓' : i+1}
            </span>
            <span className={cx('font-medium truncate', active ? 'text-ink-900' : done ? 'text-ink-700' : 'text-ink-500')}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value, mono=false, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-ink-500 flex items-center gap-0.5">{label}</div>
      <div className={cx('text-[13px] text-ink-900 mt-1', mono && 'mono')}>{children || value || '—'}</div>
    </div>
  );
}

// Qué dice la regla de rechazo automático de este presupuesto. Va dentro del campo "Seguimiento" (donde está el
// semáforo) para no sumar una fila: el texto es chico y gris, y solo se pinta de ámbar cuando faltan 7 días o menos.
function NotaSeguimiento({ q }) {
  const t = textoSeguimiento(seguimientoDe(q));
  if (!t) return null;
  return <div className={cx('mt-1 text-[11px] leading-snug', t.aviso ? 'text-amber-700 font-medium' : 'text-ink-400')}>{t.txt}</div>;
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 border-b border-line px-6">
      {tabs.map(t => (
        <button key={t.id} onClick={()=>onChange(t.id)} className={cx(
          'px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px',
          active===t.id ? 'border-brand text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-900'
        )}>
          {t.label} {t.count != null && <span className="ml-1 text-[11px] text-ink-400 font-semibold">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ── Autocomplete de artículos por código/descripción ─────────────────────
function ArticleSearchInput({ value, onChange, onSelect, placeholder = 'SKU', autoFocus = false }) {
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const debounceRef           = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length >= 2) {
      debounceRef.current = setTimeout(() => {
        CrmApi.searchArticles(val)
          .then(r => { setResults(r); setOpen(r.length > 0); })
          .catch(() => {});
      }, 200);
    } else {
      setResults([]); setOpen(false);
    }
  };

  const pick = (a) => {
    onSelect(a);
    setResults([]); setOpen(false);
  };

  return (
    <div className="relative">
      <input
        autoFocus={autoFocus}
        className="inp text-xs py-0.5 w-full"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
      />
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-[400px] bg-white border border-line rounded-xl shadow-pop max-h-56 overflow-y-auto scroll-thin">
          {results.map(a => (
            <button
              key={a.code}
              type="button"
              onMouseDown={() => pick(a)}
              className="w-full text-left px-3 py-2 hover:bg-surface border-b border-line last:border-0 flex flex-col"
            >
              <div className="flex items-center gap-2">
                <span className="text-[11px] mono font-semibold text-blue-600">{a.code}</span>
                {a.category && <span className="text-[10px] text-ink-400 truncate">{a.category}</span>}
              </div>
              <div className="text-[12px] text-ink-700 leading-snug line-clamp-1">{a.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Badge de catálogo — verifica SKU y permite agregar/quitar del catálogo ──
function CatalogBadge({ sku, description }) {
  const { pushToast } = useApp();
  const [status, setStatus]       = useState('loading'); // 'loading' | 'found' | 'missing'
  const [showModal, setShowModal] = useState(false);
  const [meta, setMeta]           = useState({ categories: [], types: [], classes: [] });

  useEffect(() => {
    if (!sku) { setStatus('none'); return; }
    CrmApi.getArticleByCode(sku)
      .then(() => setStatus('found'))
      .catch(() => setStatus('missing'));
  }, [sku]);

  const handleAdd = () => {
    CrmApi.getArticleMeta().then(setMeta).catch(() => {});
    setShowModal(true);
  };

  const handleSaved = (saved) => {
    setShowModal(false);
    setStatus('found');
    pushToast(`${saved.code} agregado al catálogo`, 'ok');
  };

  const handleRemove = async () => {
    if (!window.confirm(`¿Quitar ${sku} del catálogo?\n\nNo afecta los ítems de esta cotización.`)) return;
    try {
      await CrmApi.deleteArticle(sku);
      setStatus('missing');
      pushToast(`${sku} eliminado del catálogo`, 'ok');
    } catch (err) {
      pushToast(err.message || 'Error', 'bad');
    }
  };

  return (
    <div className="flex items-center gap-1">
      <span>{sku || '—'}</span>
      {sku && status === 'found' && (
        <>
          <span title="En catálogo" className="text-green-500 text-[10px]">✓</span>
          <button onClick={handleRemove}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-red-400 hover:text-red-600 ml-0.5">
            ×cat
          </button>
        </>
      )}
      {sku && status === 'missing' && (
        <>
          <span title="No en catálogo" className="text-amber-400 text-[10px]">?</span>
          <button onClick={handleAdd}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-brand font-semibold hover:text-brand-dark ml-0.5">
            +cat
          </button>
        </>
      )}
      {showModal && (
        <ArticleFormModal
          article={{ code: sku, description: description || '' }}
          meta={meta}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Tab Ítems completo (PRESUPUESTO: vista, OC/NP: checklist editable) ──────
function OCItemsTab({ q, detailItems, setDetailItems }) {
  const { pushToast } = useApp();
  const isOC = q.mailType === 'OC' || q.mailType === 'NOTA_PEDIDO';

  // Estado para fila de edición inline
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState({});

  // Estado para nueva fila
  const [addingNew, setAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({ sku: '', description: '', quantity: 1, unitPrice: '' });

  // (La verificación de catálogo se maneja individualmente en cada <CatalogBadge/>)

  const toggleChecked = async (it) => {
    const newVal = !it.checked;
    setDetailItems(prev => prev.map(i => i.id === it.id ? {...i, checked: newVal} : i));
    try {
      await CrmApi.updateQuoteItem(q.id, it.id, { checked: newVal });
    } catch {
      setDetailItems(prev => prev.map(i => i.id === it.id ? {...i, checked: !newVal} : i));
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditVal({ sku: it.sku || '', description: it.description, quantity: it.quantity, unitPrice: it.unitPrice ?? '' });
  };

  const saveEdit = async (it) => {
    const data = {
      sku:         editVal.sku || null,
      description: editVal.description || it.description,
      quantity:    parseFloat(editVal.quantity) || 1,
      unitPrice:   editVal.unitPrice !== '' ? parseFloat(editVal.unitPrice) : null,
    };
    const total = data.unitPrice != null ? data.unitPrice * data.quantity : it.total;
    setDetailItems(prev => prev.map(i => i.id === it.id ? {...i, ...data, total} : i));
    setEditingId(null);
    try {
      await CrmApi.updateQuoteItem(q.id, it.id, data);
    } catch (err) {
      // Revertir al valor original: si no hacemos esto, la fila queda mostrando
      // datos que el usuario cree guardados pero el servidor nunca persistió.
      setDetailItems(prev => prev.map(i => i.id === it.id ? it : i));
      pushToast(err.message || 'Error al guardar — se revirtió el cambio', 'bad');
    }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.description}"? Esta acción no se puede deshacer.`)) return;
    setDetailItems(prev => prev.filter(i => i.id !== it.id));
    try {
      await CrmApi.deleteQuoteItem(q.id, it.id);
    } catch (err) {
      pushToast(err.message || 'Error al eliminar', 'bad');
    }
  };

  const addItem = async () => {
    if (!newItem.description.trim()) return;
    try {
      const created = await CrmApi.createQuoteItem(q.id, {
        description: newItem.description.trim(),
        sku:         newItem.sku.trim() || null,
        quantity:    parseFloat(newItem.quantity) || 1,
        unitPrice:   newItem.unitPrice !== '' ? parseFloat(newItem.unitPrice) : null,
      });
      setDetailItems(prev => [...prev, created]);
      setNewItem({ sku: '', description: '', quantity: 1, unitPrice: '' });
      setAddingNew(false);
    } catch (err) {
      pushToast(err.message || 'Error al agregar ítem', 'bad');
    }
  };

  if (detailItems.length === 0 && !addingNew) {
    return (
      <div className="p-6 flex flex-col items-center gap-3 py-12">
        <div className="text-[13px] text-ink-400">Sin ítems registrados</div>
        {isOC && (
          <button onClick={() => setAddingNew(true)} className="btn-ghost text-[12px] text-brand flex items-center gap-1">
            <Icon name="plus" size={13}/> Agregar ítem
          </button>
        )}
      </div>
    );
  }

  // Total de la OC = ítems checked
  const totalOC = isOC
    ? detailItems.filter(i => i.checked).reduce((s, i) => s + (i.total || 0), 0)
    : detailItems.filter(i => i.accepted !== false).reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="p-6">
      {isOC && detailItems.length > 0 && (
        <div className="text-[12px] text-ink-500 mb-3 flex items-center gap-2">
          <Icon name="check-square" size={13}/>
          Chequeá los ítems que el cliente confirmó en esta OC. Podés editar cantidades y precios.
        </div>
      )}
      <table className="tbl w-full bg-white border border-line rounded-xl overflow-hidden">
        <thead>
          <tr>
            {isOC && <th className="w-8 !px-2"></th>}
            <th className="w-24">SKU</th>
            <th>Descripción</th>
            <th className="!text-right w-20">Cant.</th>
            <th className="!text-right w-28">P. Unit. ({q.currency === 'ARS' ? 'AR$' : 'U$S'})</th>
            <th className="!text-right w-28">Total ({q.currency === 'ARS' ? 'AR$' : 'U$S'})</th>
            {isOC && <th className="w-16"></th>}
          </tr>
        </thead>
        <tbody>
          {detailItems.map((it) => {
            const unchecked = isOC && !it.checked;
            const ncItem    = !isOC && it.accepted === false;
            if (editingId === it.id) {
              return (
                <tr key={it.id} className="bg-sky-50">
                  {isOC && <td className="!px-2"><input type="checkbox" checked={it.checked} readOnly className="opacity-40"/></td>}
                  <td>
                    <ArticleSearchInput
                      value={editVal.sku}
                      onChange={val => setEditVal(v => ({...v, sku: val}))}
                      onSelect={a => setEditVal(v => ({...v, sku: a.code, description: a.description}))}
                    />
                  </td>
                  <td><input className="inp text-xs py-0.5 w-full" value={editVal.description}
                    onChange={e => setEditVal(v => ({...v, description: e.target.value}))}/></td>
                  <td><input type="number" className="inp text-xs py-0.5 w-full text-right" value={editVal.quantity}
                    onChange={e => setEditVal(v => ({...v, quantity: e.target.value}))}/></td>
                  <td><input type="number" className="inp text-xs py-0.5 w-full text-right" value={editVal.unitPrice} placeholder="—"
                    onChange={e => setEditVal(v => ({...v, unitPrice: e.target.value}))}/></td>
                  <td className="mono text-right text-ink-400 text-xs">
                    {editVal.unitPrice !== '' && editVal.quantity
                      ? (parseFloat(editVal.unitPrice) * parseFloat(editVal.quantity)).toLocaleString('es-AR')
                      : '—'}
                  </td>
                  {isOC && (
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => saveEdit(it)} className="btn-primary text-[11px] py-0.5 px-1.5">OK</button>
                        <button onClick={() => setEditingId(null)} className="btn-ghost text-[11px] py-0.5 px-1.5">✕</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            }
            return (
              <tr key={it.id} className={cx(
                'group',
                unchecked && 'opacity-50',
                ncItem    && 'opacity-40 bg-ink-50'
              )}>
                {isOC && (
                  <td className="!px-2">
                    <input type="checkbox" checked={!!it.checked} className="cursor-pointer accent-brand"
                      onChange={() => toggleChecked(it)}/>
                  </td>
                )}
                <td className={cx('mono text-[12px]', unchecked && 'line-through')}>
                  <CatalogBadge sku={it.sku} description={it.description}/>
                </td>
                <td className={cx(unchecked && 'line-through')}>
                  {ncItem && <span className="text-[10px] font-bold text-red-500 mr-1.5 bg-red-50 border border-red-200 px-1 rounded">NC</span>}
                  {it.description}
                </td>
                <td className={cx('mono text-right', unchecked && 'line-through')}>{it.quantity}</td>
                <td className={cx('mono text-right', unchecked && 'line-through')}>
                  {it.unitPrice != null ? it.unitPrice.toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}
                </td>
                <td className={cx('mono text-right font-semibold', unchecked && 'line-through')}>
                  {it.total != null ? it.total.toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}
                </td>
                {isOC && (
                  <td>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(it)} className="btn-ghost p-1" title="Editar">
                        <Icon name="pencil" size={12} className="text-ink-500"/>
                      </button>
                      <button onClick={() => deleteItem(it)} className="btn-ghost p-1" title="Eliminar">
                        <Icon name="trash-2" size={12} className="text-red-400"/>
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}

          {/* Fila para agregar nuevo ítem */}
          {isOC && addingNew && (
            <tr className="bg-surface border-t border-dashed border-line">
              <td className="!px-2"><Icon name="plus" size={12} className="text-ink-400"/></td>
              <td>
                <ArticleSearchInput
                  autoFocus
                  value={newItem.sku}
                  placeholder="SKU"
                  onChange={val => setNewItem(n => ({...n, sku: val}))}
                  onSelect={a => setNewItem(n => ({...n, sku: a.code, description: a.description}))}
                />
              </td>
              <td><input className="inp text-xs py-0.5 w-full" placeholder="Descripción *"
                value={newItem.description} onChange={e => setNewItem(n => ({...n, description: e.target.value}))}/></td>
              <td><input type="number" className="inp text-xs py-0.5 w-full text-right" placeholder="1"
                value={newItem.quantity} onChange={e => setNewItem(n => ({...n, quantity: e.target.value}))}/></td>
              <td><input type="number" className="inp text-xs py-0.5 w-full text-right" placeholder="—"
                value={newItem.unitPrice} onChange={e => setNewItem(n => ({...n, unitPrice: e.target.value}))}/></td>
              <td className="mono text-right text-ink-400 text-xs">
                {newItem.unitPrice !== '' && newItem.quantity
                  ? (parseFloat(newItem.unitPrice) * parseFloat(newItem.quantity)).toLocaleString('es-AR') : '—'}
              </td>
              <td>
                <div className="flex gap-1">
                  <button onClick={addItem} className="btn-primary text-[11px] py-0.5 px-1.5">Agregar</button>
                  <button onClick={() => setAddingNew(false)} className="btn-ghost text-[11px] py-0.5 px-1.5">✕</button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t border-line bg-surface">
            {isOC && <td/>}
            <td colSpan={isOC ? 4 : 4} className="text-right text-[12px] text-ink-500 font-semibold py-2 pr-2">
              {isOC ? 'Total OC (confirmado)' : 'Total presupuesto'}
            </td>
            <td className="mono text-right font-bold text-[13px] pr-3 py-2">
              {fmtMoney(totalOC, q.currency, 2)}
            </td>
            {isOC && (
              <td className="!px-2">
                <button onClick={() => setAddingNew(true)}
                  className="btn-ghost p-1 text-brand" title="Agregar ítem">
                  <Icon name="plus" size={14}/>
                </button>
              </td>
            )}
          </tr>
        </tfoot>
      </table>

    </div>
  );
}

// ─── Selector "Desde" reutilizable para recordatorios ─────────────────────────
function ReminderFromSelector({ quoteId }) {
  const [accounts, setAccounts] = React.useState([]);
  const [selected, setSelected] = React.useState('');
  React.useEffect(() => {
    CrmApi.getSendAccounts().then(data => {
      setAccounts(data.accounts || []);
      setSelected(data.defaultAccount || '');
    }).catch(() => {});
  }, []);
  if (!accounts.length) return null;
  return (
    <div>
      <label className="block text-[11px] font-medium text-ink-500 mb-1">Desde</label>
      <select id={'reminder-from-' + quoteId} className="inp w-full text-[13px]" value={selected} onChange={e => setSelected(e.target.value)}>
        {accounts.map(acc => <option key={acc} value={acc}>{acc}</option>)}
      </select>
    </div>
  );
}

// ─── Modal: Enviar presupuesto por email ─────────────────────────────────────
// Cómo entró el pedido, para mostrarlo en la ficha de los casos cargados a mano
const SOURCE_LABELS = { EMAIL: 'Mail', WHATSAPP: 'WhatsApp', PHONE: 'Teléfono', WEB: 'Portal de licitación', MANUAL: 'Carga manual' };

function SendEmailModal({ quote, attachments, onClose, onSent, isSolicitud }) {
  const { pushToast } = useApp();
  const [templates, setTemplates]       = React.useState([]);
  const [selTemplate, setSelTemplate]   = React.useState('');
  const [to, setTo]                     = React.useState('');
  const [cc, setCc]                     = React.useState('');
  const [subject, setSubject]           = React.useState('');
  const [body, setBody]                 = React.useState('');
  const [sending, setSending] = React.useState(false);
  // Solicitud: tras abrir Gmail, esperamos confirmación manual antes de registrar nada
  const [gmailOpened, setGmailOpened] = React.useState(false);
  const [confirming,  setConfirming]  = React.useState(false);

  const pdfAtt = (attachments || []).find(a => a.mimeType === 'application/pdf' || a.filename?.endsWith('.pdf'));

  // Cargar plantillas y CC default al abrir — solo las que aplican a este tipo de mail
  React.useEffect(() => {
    CrmApi.getEmailTemplates().then(({ templates: tpls, ccDefault }) => {
      const wanted = isSolicitud ? 'SOLICITUD' : 'PRESUPUESTO';
      const filtered = (tpls || []).filter(t => !t.appliesTo || t.appliesTo === wanted || t.appliesTo === 'ALL');
      setTemplates(filtered);
      setCc(ccDefault || '');
      if (filtered.length > 0) applyTpl(filtered[0], filtered);
    }).catch(() => {});
    // Pre-fill destinatario con email del cliente
    if (quote.clientEmail) setTo(quote.clientEmail);
  }, []);

  const buildVars = (tpls) => {
    const today = new Date();
    const fecha = today.toLocaleDateString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric' });
    return {
      cliente:         quote.clientName || quote.client || '',
      codigo:          quote.code || '',
      np_flexxus:      quote.flexxus || '',
      vendedor:        quote.sellerName || quote.seller || '',
      asunto_original: quote.emailSubject || '',
      fecha,
    };
  };

  const applyTpl = (tpl, tpls) => {
    const vars = buildVars(tpls);
    const subst = (t) => t
      .replace(/\{cliente\}/g,         vars.cliente)
      .replace(/\{codigo\}/g,          vars.codigo)
      .replace(/\{np_flexxus\}/g,      vars.np_flexxus)
      .replace(/\{np_line\}/g,         vars.np_flexxus ? ` (NP Flexxus: ${vars.np_flexxus})` : '')
      .replace(/\{vendedor\}/g,        vars.vendedor)
      .replace(/\{asunto_original\}/g, vars.asunto_original)
      .replace(/\{fecha\}/g,           vars.fecha);
    setSelTemplate(tpl.id);
    setSubject(subst(tpl.subject));
    setBody(subst(tpl.body));
  };

  const handleSelectTemplate = (id) => {
    const tpl = templates.find(t => t.id === id);
    if (tpl) applyTpl(tpl, templates);
  };

  // Construye la URL de Gmail compose con todos los campos pre-cargados
  const buildGmailUrl = () => {
    const params = new URLSearchParams();
    if (to.trim())      params.set('to', to.trim());
    if (cc.trim())      params.set('cc', cc.trim());
    if (subject.trim()) params.set('su', subject.trim());
    if (body.trim())    params.set('body', body.trim());
    params.set('view', 'cm');
    params.set('fs', '1');
    return `https://mail.google.com/mail/?${params.toString()}`;
  };

  const handleOpenGmail = async () => {
    if (!to.trim())      { pushToast('El destinatario (Para) es requerido', 'bad'); return; }
    if (!subject.trim()) { pushToast('El asunto es requerido', 'bad'); return; }
    if (!body.trim())    { pushToast('El cuerpo es requerido', 'bad'); return; }

    // Abrir Gmail en nueva pestaña
    window.open(buildGmailUrl(), '_blank');

    if (isSolicitud) {
      // No sabemos si el vendedor va a mandar el mail o no — esperamos que lo confirme
      // a mano en vez de asumir que "abrir Gmail" significa "enviado".
      setGmailOpened(true);
      return;
    }

    // Presupuesto/OC: comportamiento sin cambios — registra y avanza etapa al toque
    setSending(true);
    try {
      const result = await CrmApi.sendQuoteEmail(quote.id, {
        to: to.trim(), cc: cc.trim(),
        subject: subject.trim(), body: body.trim(),
        _gmailOnly: true,
      });
      pushToast(`Gmail abierto${result.stageAdvanced ? ' · Etapa → Enviado' : ''}`, 'ok');
      onSent && onSent(result);
    } catch (_) {
      // No bloqueamos si falla el log — el mail ya se abrió
    } finally {
      setSending(false);
    }
    onClose();
  };

  // Solicitud: el vendedor confirma manualmente que ya mandó el mail desde Gmail
  const handleConfirmSent = async () => {
    setConfirming(true);
    try {
      const result = await CrmApi.sendQuoteEmail(quote.id, {
        to: to.trim(), cc: cc.trim(),
        subject: subject.trim(), body: body.trim(),
        _gmailOnly: true,
      });
      pushToast('Acuse de recibo confirmado', 'ok');
      onSent && onSent(result);
    } catch (err) {
      pushToast('No se pudo registrar la confirmación: ' + err.message, 'bad');
    } finally {
      setConfirming(false);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-[2px]" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-pop w-full max-w-xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">
              {isSolicitud ? 'Acuse de recibo' : 'Enviar presupuesto'}
            </div>
            <h3 className="text-[15px] font-bold text-ink-900 mt-0.5">{quote.code}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-surface flex items-center justify-center text-ink-500">
            <Icon name="x" size={16}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scroll-thin px-5 py-4 space-y-3">
          {/* Template selector */}
          {templates.length > 0 && (
            <div>
              <label className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide mb-1 block">Plantilla</label>
              <select className="inp w-full text-sm" value={selTemplate} onChange={e => handleSelectTemplate(e.target.value)}>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Para */}
          <div>
            <label className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide mb-1 block">Para *</label>
            <input className="inp w-full text-sm" type="email" placeholder="cliente@empresa.com"
              value={to} onChange={e => setTo(e.target.value)}/>
          </div>

          {/* CC */}
          <div>
            <label className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide mb-1 block">CC</label>
            <input className="inp w-full text-sm" type="text" placeholder="ventas@myselec.com.ar, ..."
              value={cc} onChange={e => setCc(e.target.value)}/>
          </div>

          {/* Asunto */}
          <div>
            <label className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide mb-1 block">Asunto *</label>
            <input className="inp w-full text-sm" type="text" placeholder="Asunto del email"
              value={subject} onChange={e => setSubject(e.target.value)}/>
          </div>

          {/* Cuerpo */}
          <div>
            <label className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide mb-1 block">Cuerpo *</label>
            <textarea className="inp w-full text-sm resize-y" rows={8} placeholder="Escribí el cuerpo del email..."
              value={body} onChange={e => setBody(e.target.value)}/>
          </div>

          {/* Adjunto que se enviará — no aplica a Solicitudes, ese flujo no lleva adjunto */}
          {!isSolicitud && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] ${pdfAtt ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              <Icon name={pdfAtt ? 'paperclip' : 'alert-circle'} size={13} className="shrink-0"/>
              {pdfAtt
                ? <span><strong>Adjunto:</strong> {pdfAtt.originalName || pdfAtt.filename.replace(/^[0-9a-f-]{36}-(\d{13}-)?/i, '')}</span>
                : <span>Sin adjunto PDF — el presupuesto no tiene ningún PDF adjunto</span>
              }
            </div>
          )}
          {/* Nota informativa */}
          <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-[12px] text-blue-800">
            <Icon name="info" size={13} className="shrink-0 mt-0.5 text-blue-500"/>
            <div>
              {isSolicitud ? (
                <div><strong>Enviar</strong> todavía no está disponible para Solicitudes — se habilita cuando se configuren las cuentas de Ventas para envío directo. Por ahora usá <strong>Abrir en Gmail</strong>.</div>
              ) : (
                <div><strong>Enviar:</strong> manda el mail desde <strong>iamyselec@gmail.com</strong>{pdfAtt ? ' con el adjunto indicado arriba.' : ' sin adjunto.'}</div>
              )}
              <div className="mt-1"><strong>Abrir en Gmail:</strong> abre un borrador en tu Gmail personal con el asunto y cuerpo precargados. El adjunto hay que cargarlo a mano.</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {gmailOpened ? (
          <div className="px-5 py-3 border-t border-line bg-amber-50">
            <div className="text-[12px] text-amber-800 mb-2.5 flex items-center gap-1.5">
              <Icon name="mail" size={13} className="shrink-0"/>
              Se abrió Gmail con el mail armado. Una vez que lo hayas mandado, confirmá acá:
            </div>
            <div className="flex items-center justify-end gap-2">
              <button className="btn-ghost" onClick={onClose} disabled={confirming}>Todavía no</button>
              <button className="btn-primary" onClick={handleConfirmSent} disabled={confirming}>
                {confirming
                  ? <><span className="inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin mr-1.5"/>Confirmando...</>
                  : <><Icon name="check" size={13}/>Ya lo envié</>
                }
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-3 border-t border-line bg-surface flex items-center justify-between gap-2">
            <button className="btn-ghost" onClick={onClose} disabled={sending}>Cancelar</button>
            <div className="flex items-center gap-2">
              <button className={cx('btn-primary', isSolicitud && 'opacity-40 grayscale cursor-not-allowed hover:opacity-40')}
                title={isSolicitud ? 'Disponible cuando se configuren las cuentas de Ventas para envío directo' : undefined}
                onClick={async () => {
                if (!to.trim())      { pushToast('El destinatario (Para) es requerido', 'bad'); return; }
                if (!subject.trim()) { pushToast('El asunto es requerido', 'bad'); return; }
                if (!body.trim())    { pushToast('El cuerpo es requerido', 'bad'); return; }
                setSending(true);
                try {
                  const result = await CrmApi.sendQuoteEmail(quote.id, {
                    to: to.trim(), cc: cc.trim(),
                    subject: subject.trim(), body: body.trim(),
                    attachmentId: pdfAtt?.id || null,
                  });
                  pushToast(`Enviado correctamente${result.stageAdvanced ? ' · Etapa → Enviado' : ''}`, 'ok');
                  onSent && onSent(result);
                  onClose();
                } catch (err) {
                  pushToast('Error al enviar: ' + err.message, 'bad');
                } finally { setSending(false); }
              }} disabled={sending || isSolicitud}>
                {sending
                  ? <><span className="inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin mr-1.5"/>Enviando...</>
                  : <><Icon name="send" size={13}/>Enviar</>
                }
              </button>
              <button className="btn-ghost border border-line" onClick={handleOpenGmail} disabled={sending}>
                <Icon name="external-link" size={13}/>Abrir en Gmail
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Popover para elegir a quién etiquetar con @ en una nota
function MentionPicker({ users, excludeId, onPick, onClose }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = users.filter(u => u.id !== excludeId && coincideBusqueda(q, u.name));
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}/>
      <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border border-line rounded-lg shadow-pop z-50 py-1">
        <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Buscar persona…"
          className="w-full px-3 py-1.5 text-[12px] outline-none border-b border-line"/>
        <div className="max-h-56 overflow-y-auto scroll-thin">
          {filtered.length === 0 && <div className="px-3 py-2 text-[12px] text-ink-400">Sin resultados</div>}
          {filtered.map(u => (
            <button key={u.id} onClick={()=>onPick(u)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface text-[12.5px]">
              <Avatar name={u.name} size={20}/>
              <span className="truncate">{u.name}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ---------- Selector de cotización para vincular (MYS-0011) ----------
// Reemplaza el desplegable chico que solo mostraba código + cliente: cuando un
// cliente tiene varias solicitudes abiertas se veían todas iguales y no había
// forma de distinguirlas. Ahora cada candidata muestra fecha, asunto del mail,
// monto y vendedor, y el panel derecho permite confirmar cuál es antes de vincular.
// El cuerpo del mail se pide al SELECCIONAR (no al pasar el mouse) y queda
// cacheado, para no disparar una consulta por cada movimiento del cursor.
// Confirmacion de desvinculacion. Antes era un window.confirm generico
// ("¿Desvincular esta cotización?") que no decia QUE se desvinculaba ni que habia
// colgando, asi que se aceptaba en automatico y se cortaban cadenas enteras
// (Solicitud -> Presupuesto -> NP -> Orden) sin querer.
function ConfirmUnlinkModal({ ownCode, target, clientName, warnings = [], onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  const confirmar = async () => {
    setSaving(true);
    try { await onConfirm(); } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} title="Desvincular" subtitle={ownCode} width={470}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn-ghost text-bad border-red-200 hover:bg-red-50"
            disabled={saving} onClick={confirmar}>
            <Icon name="unlink" size={13}/>{saving ? 'Desvinculando…' : 'Desvincular'}
          </button>
        </>
      }>
      <div className="space-y-3">
        <div className="text-[13px] text-ink-700">
          Vas a cortar el vínculo entre <b className="mono">{ownCode}</b> y <b className="mono">{target.code}</b>
          {clientName ? <> — {clientName}</> : null}.
        </div>
        <div className="px-3 py-2.5 bg-surface border border-line rounded-xl">
          <div className="flex items-center gap-2 text-[12px]">
            <span className="mono font-semibold text-ink-900">{target.code}</span>
            {target.mailType && <Badge tone={target.mailType === 'SOLICITUD' ? 'sky' : 'blue'}>{target.mailType}</Badge>}
          </div>
          {target.emailSubject && <div className="text-[11.5px] text-ink-500 mt-1 truncate">{target.emailSubject}</div>}
        </div>
        {warnings.length > 0 && (
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="text-[12px] font-semibold text-amber-800 mb-1">Ojo con esto:</div>
            <ul className="text-[11.5px] text-amber-900 space-y-0.5 list-disc pl-4">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        <div className="text-[11.5px] text-ink-500">
          El historial de cada una se conserva, pero dejan de verse como un mismo caso.
          Se puede volver a vincular después.
        </div>
      </div>
    </Modal>
  );
}

function LinkQuotePicker({ onClose, candidates, title, subtitle, emptyText, confirmLabel = 'Vincular', onConfirm, saving, clientCode, clientName }) {
  // Arranca acotado al cliente de la ficha actual — que es el caso normal. Se puede
  // pasar a "Todos" para el resto (p. ej. si el cliente se detecto mal en el mail).
  const [onlyClient, setOnlyClient] = useState(!!clientCode);
  const [search, setSearch] = useState('');
  const [selId,  setSelId]  = useState(null);
  const [cache,  setCache]  = useState({});
  const [loadingDetail, setLoadingDetail] = useState(false);

  const sameClient = clientCode ? candidates.filter(x => x.client === clientCode) : [];
  const base = (clientCode && onlyClient) ? sameClient : candidates;
  const needle = search.trim().toLowerCase();
  const list = !needle ? base : base.filter(x =>
    coincideBusqueda(needle, x.code, x.clientName, x.emailSubject, x.flexxus)
  );

  const selected = candidates.find(x => x.id === selId) || null;
  const detail   = selId ? cache[selId] : null;

  const pick = async (item) => {
    setSelId(item.id);
    if (cache[item.id]) return;
    setLoadingDetail(true);
    try {
      const d = await CrmApi.getQuoteDetail(item.id);
      setCache(c => ({ ...c, [item.id]: d }));
    } catch (_) {
      setCache(c => ({ ...c, [item.id]: { __error: true } }));
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <Modal onClose={onClose} title={title} subtitle={subtitle} width={880}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!selId || saving} onClick={() => onConfirm(selId)}>
            <Icon name="link" size={14}/>{saving ? 'Vinculando…' : confirmLabel}
          </button>
        </>
      }>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.05fr)', height: '56vh' }}>

        {/* Lista de candidatas */}
        <div className="flex flex-col min-h-0 border border-line rounded-xl overflow-hidden">
          <div className="p-2 border-b border-line bg-surface space-y-2">
            {clientCode && (
              <div className="flex items-center gap-1 p-0.5 bg-white border border-line rounded-lg">
                <button onClick={() => setOnlyClient(true)}
                  className={cx('flex-1 min-w-0 text-[11.5px] py-1 px-2 rounded-md font-medium truncate transition-colors',
                    onlyClient ? 'bg-brand text-white' : 'text-ink-600 hover:bg-surface')}>
                  Este cliente ({sameClient.length})
                </button>
                <button onClick={() => setOnlyClient(false)}
                  className={cx('flex-1 min-w-0 text-[11.5px] py-1 px-2 rounded-md font-medium truncate transition-colors',
                    !onlyClient ? 'bg-brand text-white' : 'text-ink-600 hover:bg-surface')}>
                  Todos ({candidates.length})
                </button>
              </div>
            )}
            <input autoFocus className="inp w-full text-xs" placeholder="Buscar por código, cliente o asunto…"
              value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scroll-thin">
            {list.length === 0 && (
              <div className="px-3 py-8 text-[12px] text-ink-400 text-center">
                {needle
                  ? 'Sin resultados para esa búsqueda'
                  : (clientCode && onlyClient)
                    ? <>No hay nada sin vincular de<br/><span className="text-ink-600">{clientName || 'este cliente'}</span></>
                    : emptyText}
                {clientCode && onlyClient && (
                  <div className="mt-2">
                    <button className="text-[11.5px] text-brand font-semibold hover:underline"
                      onClick={() => setOnlyClient(false)}>
                      Buscar en todos ({candidates.length})
                    </button>
                  </div>
                )}
              </div>
            )}
            {list.map(x => (
              <button key={x.id} onClick={() => pick(x)}
                className={cx('w-full text-left px-3 py-2.5 border-b border-line last:border-b-0 transition-colors',
                  selId === x.id ? 'bg-brandSoft' : 'hover:bg-surface')}>
                <div className="flex items-center gap-2">
                  <span className="mono text-[12px] font-semibold text-ink-900">{x.code}</span>
                  {x.flexxus && <span className="mono text-[10px] text-ink-500 truncate">{x.flexxus}</span>}
                  <span className="ml-auto text-[11px] text-ink-500 shrink-0">
                    {fmtDate(x.ingreso)}{x.dias != null ? ` · ${x.dias}d` : ''}
                  </span>
                </div>
                <div className="text-[12.5px] text-ink-800 truncate mt-0.5">{x.clientName || 'Sin cliente'}</div>
                {x.emailSubject && <div className="text-[11px] text-ink-500 truncate mt-0.5">{x.emailSubject}</div>}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-ink-500 truncate">{x.sellerName || 'Sin vendedor'}</span>
                  {x.monto != null && (
                    <span className="ml-auto text-[11.5px] font-semibold text-ink-700 shrink-0">{fmtMoney(x.monto, x.currency, 2)}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="px-3 py-1.5 border-t border-line bg-surface text-[11px] text-ink-500">
            {list.length} de {base.length} disponibles
          </div>
        </div>

        {/* Detalle de la seleccionada */}
        <div className="flex flex-col min-h-0 border border-line rounded-xl overflow-hidden bg-surface/30">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
              <Icon name="mouse-pointer-click" size={22} className="text-ink-300"/>
              <div className="text-[12px] text-ink-400">Elegí una de la lista para ver el detalle<br/>y confirmar que es la correcta</div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto scroll-thin p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mono text-[13px] font-bold text-ink-900">{selected.code}</span>
                {selected.mailType && <Badge tone={selected.mailType === 'SOLICITUD' ? 'sky' : 'blue'}>{selected.mailType}</Badge>}
              </div>
              <div className="text-[13px] font-semibold text-ink-900 mt-2">{selected.clientName || 'Sin cliente'}</div>
              {(selected.clientCity || selected.clientProvince) && (
                <div className="text-[11px] text-ink-500">{[selected.clientCity, selected.clientProvince].filter(Boolean).join(', ')}</div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3">
                <Field label="Ingreso"  value={fmtDateTime(selected.ingreso)}/>
                <Field label="Vendedor" value={selected.sellerName || 'Sin asignar'}/>
                <Field label="Monto"    value={selected.monto != null ? fmtMoney(selected.monto, selected.currency, 2) : '—'}/>
                <Field label="Adjuntos" value={`${selected.adj || 0} ${(selected.adj || 0) === 1 ? 'archivo' : 'archivos'}`}/>
              </div>
              <div className="mt-3 pt-3 border-t border-line">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-1.5">Mail de origen</div>
                {selected.emailSubject
                  ? <div className="text-[12px] text-ink-800 font-medium">{selected.emailSubject}</div>
                  : <div className="text-[12px] text-ink-400">Sin asunto (carga manual)</div>}
                {selected.emailFrom && <div className="text-[11px] text-ink-500 mt-0.5">De: {selected.emailFrom}</div>}
                <div className="mt-2">
                  {loadingDetail && !detail && <div className="text-[11.5px] text-ink-400">Cargando el cuerpo del mail…</div>}
                  {detail && detail.__error && <div className="text-[11.5px] text-bad">No se pudo cargar el cuerpo del mail.</div>}
                  {detail && !detail.__error && (
                    detail.emailBody
                      ? <pre className="text-[11.5px] text-ink-700 whitespace-pre-wrap break-words bg-white border border-line rounded-lg p-2.5 max-h-52 overflow-y-auto scroll-thin" style={{ fontFamily: 'inherit' }}>{detail.emailBody}</pre>
                      : <div className="text-[11.5px] text-ink-400">Este registro no tiene cuerpo de mail guardado.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Qué se puede vincular desde una ficha, según lo que ya tenga el paquete. El criterio es el
// paquete y NO el puntero `linkedQuoteId`: ese puntero a veces lo ocupa otro documento (el
// presupuesto apunta a su solicitud) y filtrar por él dejaba afuera justo lo que había que
// ofrecer — una Nota de Pedido no podía engancharse a un presupuesto que ya tenía solicitud
// (MYS-0025). Varias NP contra el mismo presupuesto es válido (compra por tandas), por eso a
// una NP se le ofrecen todos los presupuestos.
const VINCULO_TEXTOS = {
  SOLICITUD:   { label: 'Solicitud origen',      icon: 'inbox',          bg: 'bg-sky-50 text-sky-600',       titulo: 'Vincular solicitud origen', vacio: 'No hay solicitudes sin vincular' },
  PRESUPUESTO: { label: 'Presupuesto vinculado', icon: 'file-text',      bg: 'bg-blue-50 text-blue-600',     titulo: 'Vincular presupuesto',      vacio: 'No hay presupuestos para vincular' },
  NOTA_PEDIDO: { label: 'Nota de Pedido',        icon: 'clipboard-list', bg: 'bg-orange-50 text-orange-600', titulo: 'Vincular nota de pedido',   vacio: 'No hay notas de pedido sin vincular' },
};

function candidatosParaVincular(quotes, q, tipo) {
  return quotes
    .filter(x => {
      if (x.id === q.id || x.mailType !== tipo || x.anulada) return false;
      if (tipo === 'PRESUPUESTO') return q.mailType === 'NOTA_PEDIDO' ? true : !x.paquete?.solicitud;
      return !x.paquete?.presupuesto;   // SOLICITUD o NOTA_PEDIDO: les falta presupuesto
    })
    .sort((a, b) => {
      // Las del mismo cliente primero — son las candidatas probables
      const am = a.client && a.client === q.client ? 0 : 1;
      const bm = b.client && b.client === q.client ? 0 : 1;
      return am !== bm ? am - bm : new Date(b.ingreso) - new Date(a.ingreso);
    });
}

function TarjetaSinVincular({ tipo, q, quotes, abierto, setAbierto, onConfirm, saving }) {
  const t = VINCULO_TEXTOS[tipo];
  return (
    <div className="px-4 py-3 bg-white border border-line rounded-xl flex items-center gap-3">
      <div className={cx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', t.bg)}>
        <Icon name={t.icon} size={15}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-0.5">{t.label}</div>
        <div className="text-[12px] text-ink-400">Sin vincular</div>
      </div>
      <div className="shrink-0">
        <button className="btn-ghost text-[12px] py-1 px-2.5" onClick={() => setAbierto(true)}>
          <Icon name="link" size={13}/>Vincular
        </button>
        {abierto && (
          <LinkQuotePicker
            onClose={() => setAbierto(false)}
            candidates={candidatosParaVincular(quotes, q, tipo)}
            title={t.titulo}
            subtitle={`${q.code} · ${q.clientName || 'sin cliente'}`}
            emptyText={t.vacio}
            clientCode={q.client}
            clientName={q.clientName}
            onConfirm={onConfirm}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

function QuoteDetail({ code, onClose, canReassign }) {
  const { quotes, clients, users, allUsers, moveQuoteStage, setQuotes, pushToast, closeModal, openModal, updateQuote, currentUserId, roleKey } = useApp();
  const q = quotes.find(x => x.code === code);
  if (!q) return null;
  const cli = clients.find(c=>c.code===q.client);
  const sel = allUsers.find(u=>u.id===q.seller);
  const stg = STAGES_F1.find(s=>s.id===q.stage);
  const isSolicitud = q.mailType === 'SOLICITUD' || (q.source === 'EMAIL' && !q.mailType);
  const isOC = q.mailType === 'OC';
  const isNotaPedido = q.mailType === 'NOTA_PEDIDO';
  const isManual = q.source !== 'EMAIL';
  // emailMessageId solo lo tienen las que entraron por correo. Las cargadas a
  // mano guardan el pedido del cliente en los mismos campos, pero se muestran
  // como "Pedido" y se pueden editar.
  const esDeMail  = !!q.emailMessageId;
  const defaultTab = isSolicitud ? 'mail' : isOC ? 'items' : 'resumen';
  const [tab, setTab] = useState(defaultTab);
  const [stageOpen, setStageOpen] = useState(false);
  const [rejectPending, setRejectPending] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [notes, setNotes] = useState([]);
  // Edición del pedido (solo solicitudes cargadas a mano)
  const [editPedido,   setEditPedido]   = useState(false);
  const [pedidoVal,    setPedidoVal]    = useState({ body:'', de:'', ref:'' });
  const [pedidoSaving, setPedidoSaving] = useState(false);
  const [notesLoading, setNotesLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [assigningClient, setAssigningClient] = useState(false);
  const [assignClientId, setAssignClientId] = useState('');
  const [assignSellerId, setAssignSellerId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientDropOpen, setClientDropOpen] = useState(false);
  const [clientesDelRemitente, setClientesDelRemitente] = useState([]);   // clientes que tienen cargada la dirección del remitente
  const [assigningSeller, setAssigningSeller] = useState(false);
  const [assignSellerIdQuick, setAssignSellerIdQuick] = useState('');
  const [assignSellerSaving, setAssignSellerSaving] = useState(false);
  const [detailItems, setDetailItems] = useState([]);
  const [detailAttachments, setDetailAttachments] = useState([]);
  const [detailEmailBody, setDetailEmailBody] = useState('');
  const [priceBreakdown, setPriceBreakdown] = useState(null); // { subtotalNeto, ivaAmount, totalPercepciones, total }
  const [emailBodyOpen, setEmailBodyOpen] = useState(false);
  const [linkedQuotes, setLinkedQuotes] = useState({ linkedQuote: null, linkedBy: [] });
  const [linkDropOpen, setLinkDropOpen] = useState(null);   // tipo de documento que se está vinculando ('SOLICITUD' | 'PRESUPUESTO' | 'NOTA_PEDIDO')
  const [linkSaving, setLinkSaving] = useState(false);
  const noteInputRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename }
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderSubject, setReminderSubject] = useState('');
  const [reminderBody, setReminderBody] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupClientId, setDupClientId] = useState('');
  const [dupClientSearch, setDupClientSearch] = useState('');
  const [dupSaving, setDupSaving] = useState(false);
  const [editingMonto, setEditingMonto] = useState(false);
  const [montoVal, setMontoVal] = useState('');
  const [montoSaving, setMontoSaving] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineVal, setDeadlineVal] = useState('');
  const [deadlineSaving, setDeadlineSaving] = useState(false);
  // La fecha límite es un día del calendario: se fija al mediodía UTC para que no
  // se lea un día menos en Argentina (UTC-3). El backend hace lo mismo. MYS-0017.
  const deadlineIso = (dia) => dia ? new Date(dia + 'T12:00:00.000Z').toISOString() : null;
  // Revisiones. Flexxus no las tiene: recotizar es armar un presupuesto nuevo.
  // En vez de editar el original y perder lo que se le mandó al cliente, se
  // copia a uno nuevo y el anterior queda anulado.
  const [revisarOpen, setRevisarOpen] = useState(false);
  const [revisarMotivo, setRevisarMotivo] = useState('');
  const [revisando, setRevisando] = useState(false);

  const reactivar = async () => {
    const otras = (q.reemplazadaPor ? [q.reemplazadaPor] : []).join(', ');
    const msg = `¿Reactivar ${q.code}? Vuelve al tablero y a los totales.`
      + (otras ? `\n\nOjo: ${otras} sigue activa, así que van a quedar las dos.` : '');
    if (!window.confirm(msg)) return;
    try {
      const r = await CrmApi.reactivarQuote(q.id);
      const fresh = await CrmApi.getQuotes();
      if (fresh) setQuotes(fresh);
      pushToast(r.tambienVivas?.length
        ? `${q.code} reactivada — revisá cuál queda, ${r.tambienVivas.join(', ')} también está activa`
        : `${q.code} reactivada`);
    } catch (err) {
      pushToast(err.message || 'Error al reactivar', 'bad');
    }
  };

  const confirmarRevision = async () => {
    setRevisando(true);
    try {
      const r = await CrmApi.revisarQuote(q.id, revisarMotivo);
      const fresh = await CrmApi.getQuotes();
      if (fresh) setQuotes(fresh);
      pushToast(`Revisión ${r.revisionNro} creada como ${r.code}`);
      setRevisarOpen(false);
      setRevisarMotivo('');
      openModal('quoteDetail', { code: r.code });
    } catch (err) {
      pushToast(err.message || 'Error al crear la revisión', 'bad');
    } finally {
      setRevisando(false);
    }
  };

  // Semáforo de seguimiento. Marca manual, no depende de ninguna fecha.
  const [prioSaving, setPrioSaving] = useState(false);
  const guardarPrioridad = async (valor) => {
    setPrioSaving(true);
    try {
      const nuevo = q.priority === valor ? null : valor;   // volver a tocar la misma la saca
      const r = await CrmApi.updateQuotePriority(q.id, nuevo);
      // La marca es del paquete: el servidor cambia todos sus documentos y avisa cuáles
      new Set([q.code, ...(r?.codes || [])]).forEach(c => updateQuote(c, { priority: nuevo }));
      pushToast(nuevo ? `Marcada como "${prioridadDe(nuevo).label}"` : 'Marca de seguimiento quitada');
    } catch (err) {
      pushToast(err.message || 'Error al guardar', 'bad');
    } finally {
      setPrioSaving(false);
    }
  };

  const guardarDeadline = async (dia) => {
    setDeadlineSaving(true);
    try {
      await CrmApi.updateQuoteDeadline(q.id, dia);
      updateQuote(q.code, { deadline: deadlineIso(dia) });
      pushToast(dia ? 'Fecha límite actualizada' : 'Fecha límite quitada');
    } catch (err) {
      pushToast(err.message || 'Error al guardar', 'bad');
    } finally {
      setDeadlineSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!dupClientId) return;
    setDupSaving(true);
    try {
      const result = await CrmApi.duplicateQuote(q.id, { clientId: dupClientId });
      pushToast(`✅ Cotización duplicada como ${result.code} para ${result.clientName}`);
      setDupOpen(false);
      setDupClientId('');
      setDupClientSearch('');
      // Refrescar lista de cotizaciones
      if (setQuotes) {
        const fresh = await CrmApi.getQuotes();
        if (fresh) setQuotes(fresh);
      }
    } catch (err) {
      pushToast(err.message || 'Error al duplicar', 'bad');
    } finally {
      setDupSaving(false);
    }
  };

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const result = await CrmApi.uploadAttachments(q.id, Array.from(files));
      // El response puede ser array (legacy) o { attachments, flexxusParsed }
      const created = Array.isArray(result) ? result : (result.attachments || []);
      const parsed  = Array.isArray(result) ? null : result.flexxusParsed;
      setDetailAttachments(prev => [...prev, ...created]);
      if (parsed) {
        // PDF de Flexxus detectado y parseado — recargar detalle completo
        pushToast(`✅ PDF Flexxus parseado: ${parsed.npCode || ''} — ${parsed.itemCount} ítem${parsed.itemCount !== 1 ? 's' : ''}`, 'ok');
        const fresh = await CrmApi.getQuoteDetail(q.id);
        if (fresh && setQuotes) setQuotes(prev => prev.map(x => x.id === fresh.id ? { ...x, ...fresh } : x));
      } else {
        pushToast(`${created.length} archivo${created.length > 1 ? 's' : ''} subido${created.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      pushToast(err.message || 'Error al subir archivo', 'bad');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (id) => {
    if (!window.confirm('¿Eliminar este adjunto? Esta acción no se puede deshacer.')) return;
    try {
      await CrmApi.deleteAttachment(id);
      setDetailAttachments(prev => prev.filter(a => a.id !== id));
      pushToast('Adjunto eliminado');
    } catch (err) {
      pushToast(err.message || 'Error al eliminar adjunto', 'bad');
    }
  };

  const handleAssignClient = async () => {
    if (!assignClientId) return;
    setAssignSaving(true);
    try {
      await CrmApi.assignQuoteClient(q.id, {
        clientId: assignClientId,
        sellerId: assignSellerId || null,
      });
      const freshQuotes = await CrmApi.getQuotes();
      setQuotes(freshQuotes);
      pushToast('Cliente actualizado correctamente');
      setAssigningClient(false);
      setAssignClientId('');
      setAssignSellerId('');
    } catch (err) {
      pushToast(err.message || 'Error al asignar cliente', 'bad');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleAssignSellerQuick = async () => {
    if (!assignSellerIdQuick) return;
    setAssignSellerSaving(true);
    try {
      await CrmApi.assignQuote(q.id, assignSellerIdQuick);
      const freshQuotes = await CrmApi.getQuotes();
      setQuotes(freshQuotes);
      pushToast('Vendedor actualizado correctamente');
      setAssigningSeller(false);
      setAssignSellerIdQuick('');
    } catch (err) {
      pushToast(err.message || 'Error al actualizar vendedor', 'bad');
    } finally {
      setAssignSellerSaving(false);
    }
  };

  const handleSelfAssign = async () => {
    setAssignSellerSaving(true);
    try {
      await CrmApi.assignQuote(q.id, currentUserId);
      const freshQuotes = await CrmApi.getQuotes();
      setQuotes(freshQuotes);
      pushToast('Te asignaste esta cotización');
      setAssigningSeller(false);
    } catch (err) {
      pushToast(err.message || 'Error al asignarte la cotización', 'bad');
    } finally {
      setAssignSellerSaving(false);
    }
  };

  const handleQuoteStage = (stageId) => {
    setStageOpen(false);
    if (stageId === 'rechazada') {
      setRejectPending(true);
    } else {
      moveQuoteStage(q.code, stageId);
    }
  };

  const submitReject = async () => {
    if (!rejectReason) return;
    try {
      await CrmApi.changeQuoteStage(q.id, 'rechazada', { rejectReason, rejectNotes });
      const fresh = await CrmApi.getQuotes();
      setQuotes(fresh);
      pushToast('Cotización marcada como rechazada');
      setRejectPending(false);
      setRejectReason('');
      setRejectNotes('');
    } catch (err) {
      pushToast(err.message || 'Error al rechazar', 'bad');
    }
  };

  useEffect(() => {
    setNotesLoading(true);
    CrmApi.getQuoteDetail(q.id)
      .then(detail => {
        setNotes(detail.notes || []);
        setHistory(detail.unifiedHistory || detail.activities || []);
        setDetailItems(detail.items || []);
        setDetailAttachments(detail.attachments || []);
        setDetailEmailBody(detail.emailBody || '');
        setLinkedQuotes({ linkedQuote: detail.linkedQuote || null, linkedBy: detail.linkedBy || [] });
        setClientesDelRemitente(detail.clientesDelRemitente || []);
        // Breakdown de precios (solo presupuestos Flexxus con datos parseados)
        if (detail.subtotalNeto != null || detail.ivaAmount != null) {
          setPriceBreakdown({
            subtotalNeto:      detail.subtotalNeto,
            ivaAmount:         detail.ivaAmount,
            totalPercepciones: detail.totalPercepciones,
            total:             detail.amount,  // amount = grand total (con IVA)
          });
        } else {
          setPriceBreakdown(null);
        }
        setNotesLoading(false);
      })
      .catch(() => setNotesLoading(false));
  }, [q.id]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const nota = await CrmApi.addQuoteNote(q.id, newNote.trim(), mentionedUsers.map(u=>u.id));
      setNotes(ns => [...ns, nota]);
      setNewNote('');
      setMentionedUsers([]);
      setQuotes(qs => qs.map(x => x.id === q.id ? {...x, notas: (x.notas||0)+1} : x));
    } catch (err) {
      pushToast(err.message || 'Error al guardar nota', 'bad');
    } finally {
      setSavingNote(false);
    }
  };

  const handleLinkQuote = async (targetId) => {
    if (!targetId) return;
    setLinkSaving(true);
    try {
      await CrmApi.linkQuote(q.id, targetId);
      const [freshQuotes, detail] = await Promise.all([CrmApi.getQuotes(), CrmApi.getQuoteDetail(q.id)]);
      setQuotes(freshQuotes);
      setLinkedQuotes({ linkedQuote: detail.linkedQuote || null, linkedBy: detail.linkedBy || [] });
      setHistory(detail.activities || []);
      setLinkDropOpen(null);
      pushToast('Cotizaciones vinculadas');
    } catch (err) {
      pushToast(err.message || 'Error al vincular', 'bad');
    } finally {
      setLinkSaving(false);
    }
  };

  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const handleUnlinkQuote = async () => {
    try {
      await CrmApi.linkQuote(q.id, null);
      const [freshQuotes, detail] = await Promise.all([CrmApi.getQuotes(), CrmApi.getQuoteDetail(q.id)]);
      setQuotes(freshQuotes);
      setLinkedQuotes({ linkedQuote: detail.linkedQuote || null, linkedBy: detail.linkedBy || [] });
      setUnlinkTarget(null);
      pushToast('Vínculo eliminado');
    } catch (err) {
      pushToast(err.message || 'Error', 'bad');
    }
  };

  // Borrado del paquete. Como ahora los tres documentos se mueven juntos,
  // borrar uno tiene que ser una decisión explícita y no un efecto colateral:
  // si la cotización es parte de un paquete se abre un selector, y si está
  // sola se pregunta y listo, como siempre.
  const [borrarOpen, setBorrarOpen] = useState(false);
  const [borrarSel,  setBorrarSel]  = useState([]);
  const [borrando,   setBorrando]   = useState(false);

  const miembrosDelPaquete = () => {
    const p = q.paquete;
    if (!p) return [];
    const m = [];
    if (p.solicitud)   m.push({ code: p.solicitud.code,   etq: 'Solicitud' });
    if (p.presupuesto) m.push({ code: p.presupuesto.code, etq: 'Presupuesto' });
    for (const np of p.notasPedido || []) m.push({ code: np.code, etq: 'Nota de pedido' });
    return m;
  };

  const handleDelete = async () => {
    const miembros = miembrosDelPaquete();
    if (miembros.length > 1) {
      setBorrarSel([q.code]);      // por defecto, solo en la que estás parado
      setBorrarOpen(true);
      return;
    }
    if (!window.confirm(`¿Eliminar la cotización ${q.code}? Esta acción no se puede deshacer.`)) return;
    try {
      await CrmApi.deleteQuote(q.id);
      setQuotes(qs => qs.filter(x => x.id !== q.id));
      pushToast('Cotización eliminada');
      closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al eliminar', 'bad');
    }
  };

  const confirmarBorrado = async () => {
    if (!borrarSel.length) return;
    setBorrando(true);
    try {
      const aBorrar = quotes.filter(x => borrarSel.includes(x.code));
      for (const doc of aBorrar) await CrmApi.deleteQuote(doc.id);
      setQuotes(qs => qs.filter(x => !borrarSel.includes(x.code)));
      pushToast(aBorrar.length === 1 ? 'Cotización eliminada' : `${aBorrar.length} documentos eliminados`);
      setBorrarOpen(false);
      if (borrarSel.includes(q.code)) closeModal();
    } catch (err) {
      pushToast(err.message || 'Error al eliminar', 'bad');
    } finally {
      setBorrando(false);
    }
  };

  const fmtBytes = (b) => {
    if (!b) return '';
    return b >= 1024*1024 ? `${(b/(1024*1024)).toFixed(1)} MB` : `${Math.round(b/1024)} KB`;
  };
  const extOf = (filename, mimeType) => {
    if (filename && filename.includes('.')) return filename.split('.').pop().toLowerCase();
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return 'xlsx';
    if (mimeType?.includes('wordprocessing') || mimeType?.includes('word')) return 'docx';
    return 'file';
  };
  const extBg = (ext) => {
    if (ext === 'pdf') return 'bg-red-500';
    if (['xlsx','xls','csv'].includes(ext)) return 'bg-emerald-600';
    if (['docx','doc'].includes(ext)) return 'bg-blue-500';
    return 'bg-slate-500';
  };

  return (
    <>
    <Drawer onClose={onClose}
      subtitle={`Fase 1 · Cotización · ${stg?.label || q.stage}`}
      title={`${code}${cli ? ` — ${cli.name}` : ''}`}
      width={960}
      headerExtras={
        <div className="flex items-center gap-2">
          <Badge tone={stg?.tone || 'gray'} dot>{stg?.label || q.stage}</Badge>
          {q.mailType && (
            <Badge tone={q.mailType==='SOLICITUD'?'sky':q.mailType==='PRESUPUESTO'?'blue':'purple'}>
              <span className="whitespace-nowrap">{{ SOLICITUD: 'Solicitud', PRESUPUESTO: 'Presupuesto', NOTA_PEDIDO: 'Nota de Pedido' }[q.mailType] || q.mailType}</span>
            </Badge>
          )}
          {q.flexxus && (
            <Badge tone="slate"><span className="mono">{q.flexxus}</span></Badge>
          )}
          <button className="btn-ghost text-brand border-brand/30 hover:bg-brand/5"
            onClick={() => setEmailModalOpen(true)}>
            <Icon name="send" size={13}/>Enviar mail
          </button>
          {q.mailType === 'PRESUPUESTO' && q.stage === 'enviado' && cli?.email && (
            <button className="btn-ghost text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={() => setReminderOpen(true)}>
              <Icon name="mail" size={13}/>Recordatorio
            </button>
          )}
          {canReassign && (
            <div className="relative">
              <button className="btn-primary" onClick={()=>setStageOpen(o=>!o)}>
                <Icon name="arrow-right" size={13}/>Mover etapa
              </button>
              {stageOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={()=>setStageOpen(false)}/>
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-pop border border-line z-20 overflow-hidden">
                    {STAGES_F1.filter(s=>s.id!==q.stage).map(s=>(
                      <button key={s.id} onClick={()=>handleQuoteStage(s.id)}
                        className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-surface flex items-center gap-2 border-b border-line last:border-b-0">
                        <StageDot tone={s.tone}/>{s.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      }
      footer={
        <>
          {/* Duplicar — solo PRESUPUESTO */}
          {q.mailType === 'PRESUPUESTO' && <div className="relative">
            <button className="btn-ghost" onClick={() => setDupOpen(o => !o)}>
              <Icon name="copy" size={13}/>Duplicar
            </button>
            {dupOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => { setDupOpen(false); setDupClientSearch(''); setDupClientId(''); }}/>
                <div className="absolute left-0 bottom-full mb-1 w-72 bg-white rounded-xl shadow-pop border border-line z-20 overflow-hidden">
                  <div className="px-4 py-3 space-y-2">
                    <div className="text-[11px] text-ink-400">Se copian todos los ítems a una nueva cotización</div>
                    <input value={dupClientSearch} onChange={e => { setDupClientSearch(e.target.value); setDupClientId(''); }}
                      placeholder="Buscar cliente…"
                      className="inp w-full text-[13px]" autoFocus/>
                    {dupClientSearch.length >= 2 && !dupClientId && (
                      <div className="max-h-[140px] overflow-y-auto border border-line rounded-lg bg-white">
                        {clients.filter(c => {
                          return coincideBusqueda(dupClientSearch, c.name, c.code, c.cuit)
                            && c.code !== q.client;
                        }).slice(0, 6).map(c => (
                          <button key={c.id} onClick={() => { setDupClientId(c.id); setDupClientSearch(c.name); }}
                            className="w-full text-left px-3 py-2 text-[13px] hover:bg-surface flex items-center gap-2 border-b border-line last:border-0">
                            <Icon name="building-2" size={13} className="text-ink-400"/>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{c.name}</div>
                              <div className="text-[11px] text-ink-400">{c.code}{c.city ? ` · ${c.city}` : ''}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {dupClientId && (
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-green-50 rounded-lg border border-green-200 text-[12px] text-green-800">
                        <Icon name="check-circle" size={14}/> {dupClientSearch}
                      </div>
                    )}
                    <button onClick={handleDuplicate} disabled={!dupClientId || dupSaving}
                      className="btn-primary w-full justify-center text-[13px]"
                      style={!dupClientId || dupSaving ? {opacity:.45, cursor:'not-allowed'} : {}}>
                      {dupSaving ? 'Duplicando…' : 'Duplicar'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>}
          {['ADMIN','DEVELOPER'].includes(CrmAuth.getUser()?.role) && (
            <button className="btn-ghost text-bad hover:bg-red-50 border-red-200" onClick={handleDelete}>
              <Icon name="trash-2" size={13}/>Eliminar
            </button>
          )}
          {q.mailType === 'PRESUPUESTO' && !q.anulada && (
            <button className="btn-ghost" onClick={() => setRevisarOpen(true)}
              title="El cliente pidió recotizar: se copia a un presupuesto nuevo y este queda anulado">
              <Icon name="copy" size={13}/>Nueva revisión
            </button>
          )}
          <input ref={fileInputRef} type="file" multiple className="hidden"
            onChange={e => handleUploadFiles(e.target.files)}/>
          <div className="flex-1"/>
          {q.mailType !== 'SOLICITUD' && (<>
            {q.stage !== 'rechazada' && (
              <button className="btn-ghost text-bad border-red-200 hover:bg-red-50" onClick={() => setRejectPending(true)}>
                Marcar rechazada
              </button>
            )}
            {q.stage !== 'aceptada' && (
              <button className="btn-accent" onClick={() => handleQuoteStage('aceptada')}>
                <Icon name="check" size={14}/>Marcar aceptada
              </button>
            )}
          </>)}
        </>
      }
    >
      {!cli && !assigningClient && (
        <div className="mx-6 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon name="alert-triangle" size={16} className="text-amber-500 shrink-0"/>
            <div>
              <div className="text-[13px] font-semibold text-amber-900">Cliente pendiente de asignar</div>
              <div className="text-[12px] text-amber-700">
                {q.emailSubject || 'Cotización ingresada sin cliente'}
                {q.emailFrom && ` · De: ${q.emailFrom}`}
              </div>
              {/* La dirección del remitente está cargada en estos clientes (en Flexxus una
                  persona puede figurar en varias razones sociales): elegir uno lo deja
                  preseleccionado en el panel de asignación */}
              {clientesDelRemitente.length > 0 && (
                <div className="mt-2">
                  <div className="text-[11.5px] text-amber-800 mb-1">
                    {clientesDelRemitente.length === 1
                      ? 'Este mail está cargado en este cliente:'
                      : `Este mail está cargado en ${clientesDelRemitente.length} clientes. ¿Para cuál es?`}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {clientesDelRemitente.map(c => (
                      <button key={c.id}
                        title={c.cuit ? `CUIT ${c.cuit}` : c.code}
                        className="text-[11.5px] px-2 py-1 rounded-lg bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => { setAssignClientId(c.id); setClientSearch(''); setClientDropOpen(false); setAssigningClient(true); }}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className="btn-primary text-xs py-1.5 px-3 shrink-0"
            onClick={() => setAssigningClient(true)}>
            <Icon name="user-plus" size={13}/>Asignar cliente
          </button>
        </div>
      )}

      {assigningClient && (() => {
        const selectedClientObj = clients.find(c => c.id === assignClientId);
        const searchTooShort = clientSearch.trim().length < 2;
        const filteredClients = searchTooShort ? [] : clients.filter(c =>
          coincideBusqueda(clientSearch, c.name, c.cuit, c.email, c.code, c.city)
        );
        return (
          <div className="mx-6 mt-4 px-4 py-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="text-[13px] font-semibold text-amber-900 mb-3">Asignar cliente a esta cotización</div>
            <div className="grid grid-cols-2 gap-3">

              {/* ── Buscador de cliente ── */}
              <div>
                <label className="text-[11px] font-medium text-ink-700 mb-1 block">Cliente *</label>
                <div className="relative">
                  <input
                    className="inp w-full text-xs"
                    placeholder="Buscar por nombre, CUIT o email…"
                    value={clientDropOpen ? clientSearch : (selectedClientObj?.name || '')}
                    onFocus={() => { setClientDropOpen(true); setClientSearch(''); }}
                    onChange={e => { setClientSearch(e.target.value); setAssignClientId(''); }}
                  />
                  {selectedClientObj && !clientDropOpen && (
                    <button
                      title="Editar cliente"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-brand"
                      onClick={() => openModal('editClient', { clientId: assignClientId })}>
                      <Icon name="pencil" size={12}/>
                    </button>
                  )}
                  {clientDropOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setClientDropOpen(false)}/>
                      <div className="absolute z-20 mt-1 w-full bg-white border border-line rounded-xl shadow-pop max-h-56 overflow-y-auto scroll-thin">
                        {searchTooShort ? (
                          <div className="px-3 py-3 text-[12.5px] text-ink-400 text-center">Escribí para buscar…</div>
                        ) : (
                          <>
                            {filteredClients.slice(0, 80).map(c => (
                              <button key={c.id}
                                className="w-full text-left px-3 py-2 hover:bg-surface border-b border-line last:border-b-0"
                                onClick={() => { setAssignClientId(c.id); setClientDropOpen(false); setClientSearch(''); }}>
                                <div className="text-[12.5px] font-medium text-ink-900">{c.name}</div>
                              </button>
                            ))}
                            {filteredClients.length === 0 && (
                              <div className="px-3 py-3 text-[12.5px] text-ink-400 text-center">Sin resultados</div>
                            )}
                          </>
                        )}
                        {/* Opción nuevo cliente */}
                        <button
                          className="w-full text-left px-3 py-2.5 text-[12.5px] text-brand font-medium hover:bg-brandSoft border-t border-line flex items-center gap-2"
                          onClick={() => { setClientDropOpen(false); setAssigningClient(false); openModal('newClient'); }}>
                          <Icon name="plus" size={12}/>Nuevo cliente
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {/* Info + botón editar del cliente seleccionado */}
                {selectedClientObj && (
                  <div className="mt-1.5 flex items-center justify-between bg-white border border-line rounded-lg px-2.5 py-1.5 text-[11.5px]">
                    <span className="text-ink-700 truncate">
                      {selectedClientObj.email || selectedClientObj.city || 'Sin email registrado'}
                    </span>
                    <button className="ml-2 flex items-center gap-1 text-brand hover:underline shrink-0"
                      onClick={() => openModal('editClient', { clientId: assignClientId })}>
                      <Icon name="pencil" size={11}/>Editar
                    </button>
                  </div>
                )}
              </div>

              {/* ── Selector de vendedor ── */}
              <div>
                <label className="text-[11px] font-medium text-ink-700 mb-1 block">Vendedor (opcional — se usa el del cliente)</label>
                <select className="inp w-full text-xs" value={assignSellerId}
                  onChange={e => setAssignSellerId(e.target.value)}>
                  <option value="">Vendedor por defecto del cliente</option>
                  {users.filter(u => u.role==='Vendedor'||u.role==='Administrador')
                    .map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-3 justify-end">
              <button className="btn-ghost text-xs" onClick={() => { setAssigningClient(false); setClientDropOpen(false); }}
                disabled={assignSaving}>Cancelar</button>
              <button className="btn-primary text-xs"
                disabled={!assignClientId || assignSaving}
                onClick={handleAssignClient}
                style={!assignClientId || assignSaving ? {opacity:.45,cursor:'not-allowed'} : {}}>
                <Icon name={assignSaving ? 'loader' : 'check'} size={13}/>
                {assignSaving ? 'Guardando...' : 'Confirmar asignación'}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Panel cambio rápido de vendedor */}
      {assigningSeller && (
        <div className="mx-6 mt-3 px-4 py-3 bg-surface border border-line rounded-xl flex items-center gap-3">
          <Icon name="user" size={14} className="text-ink-400 shrink-0"/>
          {roleKey === 'seller' ? (
            <>
              <span className="text-xs text-ink-600 flex-1">¿Asignarte esta cotización?</span>
              <button className="btn-primary text-[11px] py-1 px-2.5 shrink-0"
                disabled={assignSellerSaving}
                style={assignSellerSaving ? {opacity:.45} : {}}
                onClick={handleSelfAssign}>
                {assignSellerSaving ? <Icon name="loader" size={12} className="animate-spin"/> : 'Asignarme esta cotización'}
              </button>
            </>
          ) : (
            <>
              <select className="inp text-xs flex-1" value={assignSellerIdQuick}
                onChange={e => setAssignSellerIdQuick(e.target.value)}
                autoFocus>
                <option value="">Seleccionar vendedor…</option>
                {users.filter(u => u.role === 'Vendedor' || u.role === 'Administrador')
                  .map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button className="btn-primary text-[11px] py-1 px-2.5 shrink-0"
                disabled={!assignSellerIdQuick || assignSellerSaving}
                style={!assignSellerIdQuick || assignSellerSaving ? {opacity:.45} : {}}
                onClick={handleAssignSellerQuick}>
                {assignSellerSaving ? <Icon name="loader" size={12} className="animate-spin"/> : 'Guardar'}
              </button>
            </>
          )}
          <button className="text-ink-400 hover:text-ink-700 shrink-0"
            onClick={() => { setAssigningSeller(false); setAssignSellerIdQuick(''); }}>
            <Icon name="x" size={14}/>
          </button>
        </div>
      )}

      {/* ── Cadena de revisiones ────────────────────────────────────────────
           Arriba de todo porque cambia cómo hay que leer el resto de la ficha:
           si está anulada, lo que se ve acá abajo ya no es lo vigente. */}
      {q.anulada && (
        <div className="mx-6 mt-4 flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-ink-100/70 border border-line">
          <Icon name="archive" size={15} className="text-ink-500 mt-0.5 shrink-0"/>
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-ink-700">
              Anulada{q.reemplazadaPor && <> — la reemplazó{' '}
                <button className="text-brand hover:underline mono"
                  onClick={() => openModal('quoteDetail', { code: q.reemplazadaPor })}>{q.reemplazadaPor}</button>
              </>}
            </div>
            <div className="text-[11.5px] text-ink-500 mt-0.5">
              {q.anuladaMotivo || 'Se armó una revisión de este presupuesto.'} Queda para consulta: no está en el tablero ni suma en los totales.
            </div>
            <button onClick={reactivar}
              className="mt-1.5 text-[11.5px] text-brand hover:underline font-medium">
              Reactivar — traerla de vuelta al tablero
            </button>
            {q.revision?.cadena?.length > 1 && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {q.revision.cadena.map((paso, i) => (
                  <React.Fragment key={paso.id}>
                    {i > 0 && <Icon name="chevron-right" size={12} className="text-ink-300 shrink-0"/>}
                    {paso.id === q.id
                      ? <span className="mono text-[11px] font-semibold text-ink-700 px-1.5 py-0.5 rounded-md bg-white border border-line">{paso.code}</span>
                      : <button onClick={() => openModal('quoteDetail', { code: paso.code })}
                          className="mono text-[11px] text-ink-500 hover:text-brand hover:underline">{paso.code}</button>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {!q.anulada && q.revisionDe && (
        <div className="mx-6 mt-4 px-3.5 py-2.5 rounded-xl bg-brandSoft/40 border border-brand/25">
          <div className="flex items-center gap-2.5">
            <Icon name="copy" size={15} className="text-brand shrink-0"/>
            <div className="text-[12.5px] font-semibold text-ink-800">
              Revisión {q.revision?.nro || 2} de este presupuesto
            </div>
          </div>
          {/* La cadena entera: cada revisión cambia el código, así que sin esto
              se pierde el hilo de por dónde pasó el presupuesto. */}
          {q.revision?.cadena?.length > 1 && (
            <div className="mt-2 pl-[25px] flex items-center gap-1.5 flex-wrap">
              {q.revision.cadena.map((paso, i) => (
                <React.Fragment key={paso.id}>
                  {i > 0 && <Icon name="chevron-right" size={12} className="text-ink-300 shrink-0"/>}
                  {paso.id === q.id
                    ? <span className="mono text-[11.5px] font-semibold text-navy-900 px-1.5 py-0.5 rounded-md bg-white border border-brand/30">{paso.code}</span>
                    : <button onClick={() => openModal('quoteDetail', { code: paso.code })}
                        className="mono text-[11.5px] text-ink-500 hover:text-brand hover:underline line-through decoration-ink-300">
                        {paso.code}
                      </button>}
                </React.Fragment>
              ))}
              {!q.revision.completa && <span className="text-[11px] text-ink-400">…</span>}
            </div>
          )}
        </div>
      )}

      {/* Pipeline strip */}
      <div className="px-6 pt-5 pb-4 bg-gradient-to-b from-surface to-white">
        <StagePipeline stages={STAGES_F1} currentId={q.stage}/>
        <div className="mt-4 grid grid-cols-4 gap-4">
          <Field label={
            <span className="flex items-center gap-1">
              Cliente
              <button title="Cambiar cliente"
                onClick={() => { setAssigningClient(true); setAssignClientId(cli?.id || ''); setAssignSellerId(''); setClientSearch(''); setClientDropOpen(false); }}
                className="text-ink-300 hover:text-brand transition-colors ml-0.5">
                <Icon name="pencil" size={10}/>
              </button>
            </span>
          } value={cli?.name || '—'}/>
          <Field label="CUIT" mono value={cli?.cuit || '—'}/>
          <Field label={
            <span className="flex items-center gap-1">
              Vendedor
              {(roleKey !== 'seller' || !sel) && (
                <button title={roleKey === 'seller' ? 'Asignarme' : 'Cambiar vendedor'}
                  onClick={() => { setAssigningSeller(true); setAssignSellerIdQuick(roleKey === 'seller' ? currentUserId : (q.seller || '')); }}
                  className="text-ink-300 hover:text-brand transition-colors ml-0.5">
                  <Icon name="pencil" size={10}/>
                </button>
              )}
            </span>
          }>
            {sel
              ? <div className="flex items-center gap-2">
                  <Avatar name={sel.name} size={20}/>{sel.name}
                  {sel.active === false && <span className="text-[11px] text-ink-400">(inactivo)</span>}
                </div>
              : <span className="text-ink-400">Sin asignar</span>}
          </Field>
          <Field label="Ingreso">
            <span className="mono">{fmtDate(q.ingreso)} <span className="text-ink-500">· hace {q.dias}d</span></span>
          </Field>
          <Field label="Seguimiento">
            {/* Un vendedor solo marca las suyas; admin y developer, cualquiera. */}
            {(roleKey !== 'seller' || q.seller === currentUserId) ? (
              <div className={cx('flex items-center gap-1', prioSaving && 'opacity-50 pointer-events-none')}>
                {PRIORIDADES.map(p => (
                  <button key={p.id} title={`${p.label} — ${p.desc}`}
                    onClick={() => guardarPrioridad(p.id)}
                    className={cx('flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[12px] transition-colors',
                      q.priority === p.id ? 'font-medium text-navy-900' : 'border-transparent text-ink-500 hover:bg-surface')}
                    style={q.priority === p.id ? { background:p.wash, borderColor:p.line } : {}}>
                    <span className="w-2 h-2 rounded-full" style={{ background:p.dot }}/>
                    {q.priority === p.id ? p.label : ''}
                  </button>
                ))}
                {!q.priority && <span className="text-ink-400 text-[12px] ml-0.5">Sin marcar</span>}
              </div>
            ) : prioridadDe(q.priority) ? (
              <span className="flex items-center gap-1.5 text-[12.5px]">
                <span className="w-2 h-2 rounded-full" style={{ background: prioridadDe(q.priority).dot }}/>
                {prioridadDe(q.priority).label}
              </span>
            ) : <span className="text-ink-400">Sin marcar</span>}
            <NotaSeguimiento q={q}/>
          </Field>
          {isSolicitud && !q.linkedQuoteId && (
            <Field label={
              <span className="flex items-center gap-1">
                Fecha límite
                {sel && (
                  <button title="Editar fecha límite"
                    onClick={() => { setDeadlineVal(q.deadline ? q.deadline.slice(0, 10) : ''); setEditingDeadline(true); }}
                    className="text-ink-300 hover:text-brand transition-colors ml-0.5">
                    <Icon name="pencil" size={10}/>
                  </button>
                )}
                {sel && q.deadline && !editingDeadline && (
                  <button title="Quitar fecha límite"
                    onClick={() => guardarDeadline(null)}
                    className="text-ink-300 hover:text-bad transition-colors">
                    <Icon name="x" size={10}/>
                  </button>
                )}
              </span>
            }>
              {editingDeadline ? (
                <span className="flex items-center gap-1.5">
                  <input autoFocus type="date"
                    className="text-[12.5px] border border-brand rounded-lg px-2 py-0.5 mono"
                    value={deadlineVal}
                    onChange={e => setDeadlineVal(e.target.value)}
                    onBlur={async () => {
                      // Salir con el casillero vacío NO borra la fecha: el campo de
                      // fecha queda vacío mientras esté incompleta, así que al tipearla
                      // a mano y hacer clic afuera se comía la que ya estaba (MYS-0017).
                      // Para quitarla a propósito está el botón "Quitar".
                      const oldVal = q.deadline ? q.deadline.slice(0, 10) : null;
                      if (deadlineVal && deadlineVal !== oldVal) {
                        await guardarDeadline(deadlineVal);
                      }
                      setEditingDeadline(false);
                    }}
                    onKeyDown={e => { if (e.key === 'Escape') setEditingDeadline(false); }}
                  />
                  {deadlineSaving && <span className="text-[11px] text-ink-400">Guardando…</span>}
                </span>
              ) : q.deadline ? (
                <span className={cx('mono', new Date(q.deadline) < new Date() && !['enviado','aceptada','rechazada'].includes(q.stage) ? 'text-bad font-semibold' : '')}>
                  {fmtDate(q.deadline)}
                </span>
              ) : sel ? (
                <span className="text-ink-400">—</span>
              ) : (
                <span className="text-ink-400 text-[12px]">Se completa al asignar vendedor</span>
              )}
            </Field>
          )}
          {isSolicitud && (
            <Field label="Acuse de recibo">
              {q.ackSentAt
                ? <span className="text-emerald-600 font-medium text-[12.5px]">✓ Enviado el {fmtDate(q.ackSentAt)}</span>
                : <span className="text-ink-400 text-[12.5px]">Pendiente</span>}
            </Field>
          )}
          {!isSolicitud && <Field label="Total con IVA" mono value={
            editingMonto ? (
              <span className="flex items-center gap-1.5">
                <input autoFocus type="number" step="0.01" min="0"
                  className="text-[12.5px] border border-brand rounded-lg px-2 py-0.5 w-36 mono"
                  value={montoVal}
                  onChange={e => setMontoVal(e.target.value)}
                  onBlur={async () => {
                    const v = parseFloat(montoVal);
                    if (!isNaN(v) && v !== q.monto) {
                      setMontoSaving(true);
                      try {
                        await CrmApi.updateQuoteAmount(q.id, v);
                        updateQuote(q.code, { monto: v });
                        pushToast('Monto actualizado');
                      } catch (err) { pushToast(err.message || 'Error al guardar', 'bad'); }
                      finally { setMontoSaving(false); }
                    }
                    setEditingMonto(false);
                  }}
                  onKeyDown={e => { if (e.key === 'Escape') setEditingMonto(false); }}
                />
                {montoSaving && <span className="text-[11px] text-ink-400">Guardando…</span>}
              </span>
            ) : (
              <span className="group flex items-center gap-1 cursor-pointer hover:text-brand"
                onClick={() => { setMontoVal(q.monto != null ? String(q.monto) : ''); setEditingMonto(true); }}
                title="Clic para editar">
                {q.monto != null ? fmtMoney(q.monto, q.currency) : '—'}
                <Icon name="pencil" size={11} className="text-ink-300 opacity-0 group-hover:opacity-100 transition-opacity"/>
              </span>
            )
          }/>}
          {!isSolicitud && <Field label={isNotaPedido ? 'Cod. Flexxus NP' : 'Cod. Flexxus PR'} mono value={q.flexxus || '—'}/>}
          {!isSolicitud && <Field label="Zona de entrega" value={cli?.zone || '—'}/>}
          {!esDeMail && isSolicitud && <Field label="Origen" value={SOURCE_LABELS[q.source] || 'Carga manual'}/>}
          <Field label="Contacto">
            {cli?.email
              ? <a href={`mailto:${cli.email}`} className="text-brand hover:underline text-[12.5px] truncate block">{cli.email}</a>
              : <span className="text-[12.5px]">—</span>}
          </Field>
        </div>
      </div>

      {q.followUpDate && new Date(q.followUpDate) < new Date() && q.stage === 'enviado' && (
        <div className="mx-6 mb-3 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
          <Icon name="clock" size={15} className="text-orange-500 shrink-0"/>
          <span className="text-[12.5px] text-orange-900">
            <span className="font-semibold">Seguimiento pendiente</span>
            {' · Alerta desde '}{fmtDate(q.followUpDate)}
          </span>
        </div>
      )}

      {revisarOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4"
             onClick={() => !revisando && setRevisarOpen(false)}>
          <div className="w-[440px] bg-white rounded-xl shadow-pop border border-line modal-enter overflow-hidden"
               onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-line">
              <div className="text-[14px] font-semibold text-navy-900">Nueva revisión de {q.code}</div>
              <div className="text-[12px] text-ink-500 mt-0.5">
                Se copia a un presupuesto nuevo con los mismos ítems. Este queda anulado.
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-[12.5px] text-ink-600 bg-surface border border-line rounded-lg px-3 py-2.5 space-y-1">
                <div>· El nuevo arranca en la misma etapa y con los mismos ítems, para editarlos.</div>
                <div>· {q.code} sale del tablero y de los totales, pero se sigue pudiendo abrir.</div>
                <div>· Si tiene solicitud o nota de pedido vinculada, pasan al nuevo.</div>
              </div>
              <FormGroup label="Motivo (opcional)" hint="Queda en el historial de las dos">
                <input className="inp w-full" autoFocus value={revisarMotivo}
                  onChange={e => setRevisarMotivo(e.target.value)}
                  placeholder="El cliente pidió agregar dos ítems"/>
              </FormGroup>
            </div>
            <div className="px-5 py-3 border-t border-line flex gap-2 justify-end bg-surface/50">
              <button className="btn-ghost" disabled={revisando}
                onClick={() => setRevisarOpen(false)}>Cancelar</button>
              <button className="btn-primary" disabled={revisando} onClick={confirmarRevision}>
                {revisando ? 'Creando…' : 'Crear la revisión'}
              </button>
            </div>
          </div>
        </div>
      )}

      {borrarOpen && (() => {
        const miembros = miembrosDelPaquete();
        const toggle = (code) => setBorrarSel(s => s.includes(code) ? s.filter(x => x !== code) : [...s, code]);
        const quedan = miembros.filter(m => !borrarSel.includes(m.code));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4"
               onClick={() => !borrando && setBorrarOpen(false)}>
            <div className="w-[420px] bg-white rounded-xl shadow-pop border border-line modal-enter overflow-hidden"
                 onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-line">
                <div className="text-[14px] font-semibold text-navy-900">¿Qué querés eliminar?</div>
                <div className="text-[12px] text-ink-500 mt-0.5">
                  Esta cotización es parte de un paquete de {miembros.length} documentos.
                </div>
              </div>
              <div className="px-5 py-3 space-y-1.5">
                {miembros.map(m => (
                  <label key={m.code}
                    className={cx('flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer border transition-colors',
                      borrarSel.includes(m.code) ? 'border-bad/40 bg-red-50/60' : 'border-line hover:bg-surface')}>
                    <input type="checkbox" checked={borrarSel.includes(m.code)}
                      onChange={() => toggle(m.code)}
                      className="w-4 h-4 rounded border-line accent-red-600"/>
                    <span className="mono text-[12px] font-semibold text-navy-900">{m.code}</span>
                    <span className="text-[11.5px] text-ink-500">{m.etq}</span>
                    {m.code === q.code && <span className="ml-auto text-[10.5px] text-ink-400">estás acá</span>}
                  </label>
                ))}
              </div>
              <div className="px-5 pb-3">
                <button onClick={() => setBorrarSel(miembros.map(m => m.code))}
                  className="text-[12px] text-bad hover:underline">Seleccionar el paquete entero</button>
              </div>
              {borrarSel.length > 0 && quedan.length > 0 && (
                <div className="mx-5 mb-3 text-[11.5px] text-ink-600 bg-surface border border-line rounded-lg px-3 py-2">
                  {quedan.map(m => m.code).join(', ')} {quedan.length === 1 ? 'queda' : 'quedan'} sin el resto del paquete.
                </div>
              )}
              <div className="px-5 py-3 border-t border-line flex gap-2 justify-end bg-surface/50">
                <button className="btn-ghost" disabled={borrando}
                  onClick={() => setBorrarOpen(false)}>Cancelar</button>
                <button className="btn-ghost text-bad hover:bg-red-50 border-red-300 disabled:opacity-50"
                  disabled={borrando || !borrarSel.length}
                  onClick={confirmarBorrado}>
                  {borrando ? 'Eliminando…' : borrarSel.length === miembros.length
                    ? 'Eliminar el paquete'
                    : `Eliminar ${borrarSel.length} ${borrarSel.length === 1 ? 'documento' : 'documentos'}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {unlinkTarget && (
        <ConfirmUnlinkModal
          ownCode={q.code}
          target={unlinkTarget}
          clientName={cli?.name || q.clientName}
          warnings={(linkedQuotes.linkedBy || [])
            .filter(x => x.mailType === 'NOTA_PEDIDO')
            .map(np => `La Nota de Pedido ${np.code} perderá la referencia.`)}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={handleUnlinkQuote}
        />
      )}

      {/* ── Vinculaciones: Solicitud / Presupuesto / NP ── */}
      {(() => {
        const solicitud   = linkedQuotes.linkedQuote?.mailType === 'SOLICITUD'   ? linkedQuotes.linkedQuote : linkedQuotes.linkedBy?.find(x => x.mailType === 'SOLICITUD');
        const presupuesto = linkedQuotes.linkedQuote?.mailType === 'PRESUPUESTO' ? linkedQuotes.linkedQuote : linkedQuotes.linkedBy?.find(x => x.mailType === 'PRESUPUESTO');
        const npLink      = (linkedQuotes.linkedQuote?.mailType === 'NOTA_PEDIDO' ? linkedQuotes.linkedQuote : null)
                         || linkedQuotes.linkedBy?.find(x => x.mailType === 'NOTA_PEDIDO');

        // Cards: siempre muestran el vínculo primario (con botón vincular si falta),
        // la NP solo aparece cuando existe. La OC salió del paquete: era un espejo
        // vacío y confundía más de lo que ayudaba.
        // ── Paquete: todos los documentos del negocio, desde cualquiera de ellos ──
        // La tarjeta del tablero abre el documento más avanzado (la NP), y esa ficha
        // no tenía cómo llegar a su presupuesto ni a su solicitud: la solicitud está
        // vinculada al presupuesto, no a la NP, así que ni venía en el detalle
        // (Diego, 14/09/2026). El paquete ya viene armado en la lista con los
        // códigos de todos, y con eso alcanza para listar y navegar.
        if (q.paquete) {
          const p = q.paquete;
          const docs = [
            p.solicitud   && { tipo: 'Solicitud',      icon: 'inbox',          bg: 'bg-sky-50 text-sky-600',       code: p.solicitud.code },
            p.presupuesto && { tipo: 'Presupuesto',    icon: 'file-text',      bg: 'bg-blue-50 text-blue-600',     code: p.presupuesto.code, amount: p.presupuesto.amount, currency: p.presupuesto.currency },
            ...(p.notasPedido || []).map(n => ({ tipo: 'Nota de Pedido', icon: 'clipboard-list', bg: 'bg-orange-50 text-orange-600', code: n.code, amount: n.amount, currency: n.currency })),
          ].filter(Boolean);
          // Vínculo directo con esta ficha: es el único que se puede desvincular desde acá
          const directos = [linkedQuotes.linkedQuote, ...(linkedQuotes.linkedBy || [])].filter(Boolean);
          // Lo que le falta a este documento para estar completo. Una solicitud sola, o una NP sola,
          // no llega acá (no es paquete) y se resuelve más abajo con las mismas tarjetas.
          const faltan = [
            q.mailType === 'PRESUPUESTO'  && !p.solicitud && 'SOLICITUD',
            q.mailType === 'PRESUPUESTO'  && !(p.notasPedido || []).length && !q.anulada && 'NOTA_PEDIDO',
            q.mailType === 'NOTA_PEDIDO'  && !p.presupuesto && 'PRESUPUESTO',
          ].filter(Boolean);
          return (
            <div className="mx-6 mb-3 space-y-2">
              <div className="bg-white border border-line rounded-xl overflow-hidden">
                <div className="px-4 pt-3 pb-1.5 text-[11px] uppercase tracking-wider font-semibold text-ink-500">
                  Documentos del negocio
                </div>
                {docs.map(d => {
                  const doc  = quotes.find(x => x.code === d.code);
                  const esta = d.code === q.code;
                  const stgD = doc && STAGES_F1.find(s => s.id === doc.stage);
                  const directo = directos.find(x => x.code === d.code && ['SOLICITUD', 'PRESUPUESTO'].includes(x.mailType));
                  return (
                    <div key={d.code} className={cx('px-4 py-2.5 flex items-center gap-3 border-t border-line/70', esta && 'bg-brandSoft/40')}>
                      <div className={cx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', d.bg)}>
                        <Icon name={d.icon} size={15}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-ink-400">{d.tipo}</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="mono text-[13px] font-semibold text-ink-900">{d.code}</span>
                          {doc?.flexxus && <Badge tone="slate"><span className="mono">{doc.flexxus}</span></Badge>}
                          {stgD && <Badge tone={stgD.tone} dot>{stgD.label}</Badge>}
                          {d.amount != null && <span className="text-[12px] mono text-ink-500">{fmtMoney(d.amount, d.currency || 'USD', 2)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {esta ? (
                          <span className="text-[11.5px] font-semibold text-brand px-2">Estás acá</span>
                        ) : doc ? (
                          // Reemplaza la ficha en vez de apilarla: ir y volver entre los
                          // documentos del negocio dejaba varias fichas una arriba de otra.
                          <button className="btn-ghost text-[12px] py-1 px-2.5"
                            onClick={() => { onClose(); setTimeout(() => openModal('quoteDetail', { code: d.code }), 80); }}>
                            Ver <Icon name="arrow-right" size={11}/>
                          </button>
                        ) : (
                          // De otro vendedor: no está en la lista de quien mira, y la ficha no abriría
                          <span className="text-[11px] text-ink-400 px-2" title="Este documento no está en tu lista">Sin acceso</span>
                        )}
                        {directo && (
                          <button onClick={() => setUnlinkTarget(directo)} className="w-7 h-7 rounded-lg hover:bg-red-50 text-ink-400 hover:text-bad flex items-center justify-center" title="Desvincular">
                            <Icon name="x" size={12}/>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {faltan.map(tipo => (
                <TarjetaSinVincular key={tipo} tipo={tipo} q={q} quotes={quotes}
                  abierto={linkDropOpen === tipo} setAbierto={v => setLinkDropOpen(v ? tipo : null)}
                  onConfirm={handleLinkQuote} saving={linkSaving}/>
              ))}
            </div>
          );
        }

        // Documento suelto (no es paquete). Cada tipo muestra lo que le falta para completarse:
        // la solicitud su presupuesto, el presupuesto su solicitud y su nota de pedido, y la
        // nota de pedido su presupuesto — esta última no tenía tarjeta y no había dónde vincularla.
        const vcards = [];
        if (q.mailType === 'PRESUPUESTO') {
          vcards.push({ label:'Solicitud origen',      icon:'inbox',          bg:'bg-sky-50 text-sky-600',       data: solicitud   || null, modal:'quoteDetail', vincularTarget:'SOLICITUD'   });
        }
        if (q.mailType === 'SOLICITUD') {
          vcards.push({ label:'Presupuesto vinculado', icon:'file-text',      bg:'bg-blue-50 text-blue-600',     data: presupuesto || null, modal:'quoteDetail', vincularTarget:'PRESUPUESTO' });
        }
        if (q.mailType === 'NOTA_PEDIDO') {
          vcards.push({ label:'Presupuesto vinculado', icon:'file-text',      bg:'bg-blue-50 text-blue-600',     data: presupuesto || null, modal:'quoteDetail', vincularTarget:'PRESUPUESTO' });
        }
        if (npLink)      vcards.push({ label:'Nota de Pedido',   icon:'clipboard-list', bg:'bg-orange-50 text-orange-600', data: npLink,                            modal:'quoteDetail', vincularTarget: null });
        else if (q.mailType === 'PRESUPUESTO' && !q.anulada) {
          vcards.push({ label:'Nota de Pedido',   icon:'clipboard-list', bg:'bg-orange-50 text-orange-600', data: null,                              modal:'quoteDetail', vincularTarget:'NOTA_PEDIDO' });
        }

        if (!vcards.length) return null;
        return (
          <div className="mx-6 mb-3 space-y-2">
            {vcards.map((vc, i) => {
              // Card sin vínculo: muestra botón Vincular
              if (!vc.data) return (
                <TarjetaSinVincular key={i} tipo={vc.vincularTarget} q={q} quotes={quotes}
                  abierto={linkDropOpen === vc.vincularTarget} setAbierto={v => setLinkDropOpen(v ? vc.vincularTarget : null)}
                  onConfirm={handleLinkQuote} saving={linkSaving}/>
              );

              // Card con vínculo
              const stg = STAGES_F1.find(s => s.id === vc.data.stage);
              return (
                <div key={i} className="px-4 py-3 bg-white border border-line rounded-xl flex items-center gap-3">
                  <div className={cx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', vc.bg)}>
                    <Icon name={vc.icon} size={15}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-0.5">{vc.label}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="mono text-[13px] font-semibold text-ink-900">{vc.data.code}</span>
                      {vc.data.flexxusCode && <Badge tone="slate"><span className="mono">{vc.data.flexxusCode}</span></Badge>}
                      {stg ? <Badge tone={stg.tone} dot>{stg.label}</Badge> : vc.data.stage ? <Badge tone="gray" dot>{vc.data.stage}</Badge> : null}
                      {vc.data.amount != null && <span className="text-[12px] mono text-ink-500">{fmtMoney(vc.data.amount, vc.data.currency||'USD', 2)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button className="btn-ghost text-[12px] py-1 px-2.5"
                      onClick={() => openModal(vc.modal, { code: vc.data.code })}>
                      Ver <Icon name="arrow-right" size={11}/>
                    </button>
                    {vc.vincularTarget && (
                      <button onClick={() => setUnlinkTarget(vc.data)} className="w-7 h-7 rounded-lg hover:bg-red-50 text-ink-400 hover:text-bad flex items-center justify-center" title="Desvincular">
                        <Icon name="x" size={12}/>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {q.source === 'EMAIL' && q.emailSubject && !isSolicitud && (
        <div className="mx-6 mb-4">
          <div className="px-4 py-3 bg-surface border border-line rounded-xl flex items-start gap-3">
            <Icon name="mail" size={15} className="mt-0.5 text-ink-500 shrink-0"/>
            <div className="min-w-0 flex-1">
              {/* Este bloque solo sale en presupuestos y notas de pedido (las solicitudes
                  tienen el suyo): decía "Solicitud recibida" en todos los casos. */}
              <div className="text-[11.5px] font-semibold text-ink-700 mb-0.5">{isNotaPedido ? 'Mail de la nota de pedido' : 'Mail del presupuesto'}</div>
              <div className="text-[12.5px] text-ink-900 truncate"><span className="text-ink-500">Asunto:</span> {q.emailSubject}</div>
              <div className="text-[12px] text-ink-500 mt-0.5"><span>De:</span> {q.emailFrom}</div>
              {detailEmailBody && (
                <button onClick={() => setEmailBodyOpen(o => !o)}
                  className="mt-2 text-[11.5px] text-brand hover:underline flex items-center gap-1">
                  <Icon name={emailBodyOpen ? 'chevron-up' : 'chevron-down'} size={11}/>
                  {emailBodyOpen ? 'Ocultar cuerpo del mail' : 'Ver cuerpo del mail'}
                </button>
              )}
            </div>
          </div>
          {emailBodyOpen && detailEmailBody && (
            <div className="mt-1 p-3 bg-surface border border-line rounded-b-xl text-[11.5px] text-ink-700 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto scroll-thin">
              {detailEmailBody}
            </div>
          )}
        </div>
      )}


      {(() => {
        // Antes las imagenes no se contaban porque el sistema no guardaba
        // ninguna. Ahora sí guarda las que traen el pedido del cliente.
        const nonImageAdj = detailAttachments;
        const tabs = isSolicitud
          ? [
              { id:'mail',     label: esDeMail ? 'Mail' : 'Pedido' },
              { id:'adj',      label:'Adjuntos', count: nonImageAdj.length > 0 ? nonImageAdj.length : null },
              { id:'historial',label:'Historial' },
              { id:'notas',    label:'Notas', count: notes.length > 0 ? notes.length : null },
            ]
          : isOC
          ? [
              { id:'items',    label:'Ítems', count: detailItems.length > 0 ? detailItems.length : null },
              { id:'mail',     label:'Mail' },
              { id:'adj',      label:'Adjuntos', count: nonImageAdj.length > 0 ? nonImageAdj.length : null },
              { id:'historial',label:'Historial' },
              { id:'notas',    label:'Notas', count: notes.length > 0 ? notes.length : null },
            ]
          : isManual
          ? [
              { id:'resumen',  label:'Resumen' },
              { id:'items',    label:'Ítems', count: detailItems.length > 0 ? detailItems.length : null },
              { id:'adj',      label:'Adjuntos', count: nonImageAdj.length > 0 ? nonImageAdj.length : null },
              { id:'historial',label:'Historial' },
              { id:'notas',    label:'Notas', count: notes.length > 0 ? notes.length : null },
            ]
          : [
              { id:'resumen',  label:'Resumen' },
              { id:'items',    label:'Ítems', count: detailItems.length > 0 ? detailItems.length : null },
              { id:'adj',      label:'Adjuntos', count: nonImageAdj.length > 0 ? nonImageAdj.length : null },
              { id:'historial',label:'Historial' },
              { id:'notas',    label:'Notas', count: notes.length > 0 ? notes.length : null },
            ];
        return <TabBar active={tab} onChange={setTab} tabs={tabs}/>;
      })()}

      {tab === 'mail' && (
        <div className="p-6">
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            {/* Mismo bloque para un mail real o para un pedido cargado a mano */}
            <div className="px-5 py-4 border-b border-line space-y-2.5">
              {editPedido ? (
                <>
                  <div className="flex items-start gap-3">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 w-20 shrink-0 pt-2">Referencia</span>
                    <input className="inp flex-1 text-[13px]" placeholder="Ej: Pedido cables 3x2.5"
                      value={pedidoVal.ref} onChange={e => setPedidoVal(v => ({...v, ref: e.target.value}))}/>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 w-20 shrink-0 pt-2">De</span>
                    <input className="inp flex-1 text-[13px]" placeholder="Ej: Juan Pérez · 351 555 1234"
                      value={pedidoVal.de} onChange={e => setPedidoVal(v => ({...v, de: e.target.value}))}/>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 w-20 shrink-0 pt-0.5">
                      {esDeMail ? 'Asunto' : 'Referencia'}
                    </span>
                    <span className="text-[14px] font-semibold text-ink-900 leading-snug flex-1">
                      {q.emailSubject || (esDeMail ? '(sin asunto)' : '—')}
                    </span>
                    {!esDeMail && isSolicitud && (
                      <button
                        onClick={() => { setPedidoVal({ body: detailEmailBody || '', de: q.emailFrom || '', ref: q.emailSubject || '' }); setEditPedido(true); }}
                        className="shrink-0 text-[12px] text-brand hover:underline flex items-center gap-1">
                        <Icon name="pencil" size={12}/>Editar
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 w-20 shrink-0">De</span>
                    <span className={cx('text-[13px] text-ink-700', esDeMail && 'mono')}>{q.emailFrom || '—'}</span>
                  </div>
                </>
              )}
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 w-20 shrink-0">Ingreso</span>
                <span className="text-[13px] text-ink-500">{fmtDate(q.ingreso)} · hace {q.dias}d</span>
              </div>
            </div>
            {/* Cuerpo */}
            <div className="px-5 py-4">
              {editPedido ? (
                <div className="space-y-3">
                  <textarea rows="12" autoFocus
                    className="inp w-full resize-none text-[12.5px] leading-relaxed"
                    placeholder="Pegá acá lo que te escribió el cliente, o escribí lo que te pidió…"
                    value={pedidoVal.body} onChange={e => setPedidoVal(v => ({...v, body: e.target.value}))}/>
                  <div className="flex items-center justify-end gap-2">
                    <button className="btn-ghost text-[12px] py-1.5 px-3" disabled={pedidoSaving}
                      onClick={() => setEditPedido(false)}>Cancelar</button>
                    <button className="btn-primary text-[12px] py-1.5 px-3" disabled={pedidoSaving}
                      onClick={async () => {
                        if (!window.confirm('Se va a reemplazar el pedido guardado por este texto. ¿Confirmás?')) return;
                        setPedidoSaving(true);
                        try {
                          await CrmApi.updateQuotePedido(q.id, pedidoVal.body, pedidoVal.de, pedidoVal.ref);
                          setDetailEmailBody(pedidoVal.body);
                          updateQuote(q.code, { emailSubject: pedidoVal.ref || null, emailFrom: pedidoVal.de || null });
                          setEditPedido(false);
                          pushToast('Pedido actualizado');
                        } catch (err) {
                          pushToast(err.message || 'Error al guardar el pedido', 'bad');
                        } finally { setPedidoSaving(false); }
                      }}>
                      <Icon name={pedidoSaving ? 'loader' : 'check'} size={12} className={pedidoSaving ? 'animate-spin' : ''}/>
                      {pedidoSaving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                </div>
              ) : detailEmailBody ? (
                <pre className="text-[12.5px] text-ink-700 whitespace-pre-wrap font-sans leading-relaxed max-h-[480px] overflow-y-auto scroll-thin">
                  {detailEmailBody}
                </pre>
              ) : esDeMail ? (
                <div className="text-[13px] text-ink-400 py-8 text-center">Sin cuerpo de mail guardado</div>
              ) : (
                <div className="text-[13px] text-ink-400 py-8 text-center">
                  Todavía no cargaste el pedido del cliente.
                  {isSolicitud && (
                    <button className="block mx-auto mt-2 text-brand hover:underline text-[13px]"
                      onClick={() => { setPedidoVal({ body:'', de: q.emailFrom || '', ref: q.emailSubject || '' }); setEditPedido(true); }}>
                      Cargarlo ahora
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'resumen' && !isSolicitud && (
        <div className="p-6">
          {detailItems.length > 0 ? (
            <div className="grid grid-cols-3 gap-5">
              <div className="col-span-2 bg-white border border-line rounded-xl p-5">
                <div className="text-sm font-semibold mb-3 text-ink-900">{isNotaPedido ? (q.flexxus ? 'Nota de Pedido Flexxus' : 'Nota de Pedido') : (q.flexxus ? 'Presupuesto Flexxus' : 'Presupuesto')}</div>
                <table className="w-full text-[12.5px]">
                  <thead><tr className="text-left text-ink-500">
                    <th className="font-semibold pb-2">SKU</th>
                    <th className="font-semibold pb-2">Descripción</th>
                    <th className="font-semibold pb-2 text-right">Cant.</th>
                    <th className="font-semibold pb-2 text-right">P. Unit. ({q.currency === 'ARS' ? 'AR$' : 'U$S'})</th>
                    <th className="font-semibold pb-2 text-right">Total ({q.currency === 'ARS' ? 'AR$' : 'U$S'})</th>
                  </tr></thead>
                  <tbody>
                    {detailItems.filter(i => i.accepted).map((it, idx) => (
                      <tr key={it.id || idx} className="border-t border-line group">
                        <td className="py-2 mono text-ink-700">
                          <CatalogBadge sku={it.sku} description={it.description}/>
                        </td>
                        <td className="py-2">{it.description}</td>
                        <td className="py-2 mono text-right">{it.quantity}</td>
                        <td className="py-2 mono text-right">{it.unitPrice != null ? it.unitPrice.toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}</td>
                        <td className="py-2 mono text-right font-semibold">{it.total != null ? it.total.toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {priceBreakdown?.subtotalNeto != null && (
                      <tr className="border-t border-line">
                        <td colSpan="4" className="text-right py-1.5 text-[11px] text-ink-400">Subtotal neto</td>
                        <td className="text-right py-1.5 mono text-[12px] text-ink-500">
                          {fmtMoney(priceBreakdown.subtotalNeto, q.currency, 2)}
                        </td>
                      </tr>
                    )}
                    {priceBreakdown?.ivaAmount != null && priceBreakdown.ivaAmount > 0 && (
                      <tr>
                        <td colSpan="4" className="text-right py-1.5 text-[11px] text-ink-400">IVA 21%</td>
                        <td className="text-right py-1.5 mono text-[12px] text-ink-500">
                          {fmtMoney(priceBreakdown.ivaAmount, q.currency, 2)}
                        </td>
                      </tr>
                    )}
                    {priceBreakdown?.totalPercepciones != null && priceBreakdown.totalPercepciones > 0 && (
                      <tr>
                        <td colSpan="4" className="text-right py-1.5 text-[11px] text-ink-400">Percepciones</td>
                        <td className="text-right py-1.5 mono text-[12px] text-ink-500">
                          {fmtMoney(priceBreakdown.totalPercepciones, q.currency, 2)}
                        </td>
                      </tr>
                    )}
                    {(q.monto != null || priceBreakdown?.total != null) && (
                      <tr className="border-t-2 border-ink-900">
                        <td colSpan="4" className="text-right pt-3 font-bold">TOTAL</td>
                        <td className="text-right pt-3 mono font-bold text-base">
                          {fmtMoney(priceBreakdown?.total ?? q.monto, q.currency, 2)}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
                {detailItems.filter(i => !i.accepted).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-line">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">No Cotiza</div>
                    <div className="space-y-1">
                      {detailItems.filter(i => !i.accepted).map((it, idx) => (
                        <div key={it.id || idx} className="flex items-center gap-3 text-[12px] text-ink-400 line-through">
                          <span className="mono">{it.sku || '—'}</span>
                          <span>{it.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="bg-white border border-line rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Resumen</div>
                  <ul className="text-[12.5px] space-y-1.5">
                    <li className="flex justify-between"><span className="text-ink-500">Cliente</span><span className="font-medium">{cli?.name || '—'}</span></li>
                    {q.flexxus && <li className="flex justify-between"><span className="text-ink-500">{isNotaPedido ? 'NP Flexxus' : 'Pres. Flexxus'}</span><span className="mono">{q.flexxus}</span></li>}
                    {priceBreakdown?.subtotalNeto != null && (
                      <li className="flex justify-between"><span className="text-ink-500">Subtotal neto</span><span className="mono">{fmtMoney(priceBreakdown.subtotalNeto, q.currency, 2)}</span></li>
                    )}
                    {priceBreakdown?.ivaAmount != null && priceBreakdown.ivaAmount > 0 && (
                      <li className="flex justify-between"><span className="text-ink-500">IVA 21%</span><span className="mono">{fmtMoney(priceBreakdown.ivaAmount, q.currency, 2)}</span></li>
                    )}
                    {priceBreakdown?.totalPercepciones != null && priceBreakdown.totalPercepciones > 0 && (
                      <li className="flex justify-between"><span className="text-ink-500">Percepciones</span><span className="mono">{fmtMoney(priceBreakdown.totalPercepciones, q.currency, 2)}</span></li>
                    )}
                    {(q.monto != null || priceBreakdown?.total != null) && (
                      <li className="flex justify-between border-t border-line pt-1.5 mt-0.5">
                        <span className="font-semibold text-ink-800">Total</span>
                        <span className="mono font-semibold">{fmtMoney(priceBreakdown?.total ?? q.monto, q.currency)}</span>
                      </li>
                    )}
                    <li className="flex justify-between"><span className="text-ink-500">Ítems</span><span>{detailItems.filter(i=>i.accepted).length} cotizados{detailItems.filter(i=>!i.accepted).length > 0 ? `, ${detailItems.filter(i=>!i.accepted).length} NC` : ''}</span></li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-5">
              <div className="col-span-2 bg-white border border-line rounded-xl p-5">
                <div className="text-sm font-semibold mb-2 text-ink-900">Presupuesto</div>
                <div className="text-[13px] text-ink-400 py-6 text-center">Sin ítems de presupuesto todavía</div>
              </div>
              <div className="space-y-4">
                <div className="bg-white border border-line rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Resumen</div>
                  <ul className="text-[12.5px] space-y-1.5">
                    <li className="flex justify-between"><span className="text-ink-500">Cliente</span><span className="font-medium">{cli?.name || '—'}</span></li>
                    <li className="flex justify-between"><span className="text-ink-500">Monto</span><span className="mono">{q.monto != null ? fmtMoney(q.monto, q.currency) : '—'}</span></li>
                    <li className="flex justify-between"><span className="text-ink-500">Ingreso</span><span className="mono">{fmtDate(q.ingreso)}</span></li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'items' && !isSolicitud && (
        <OCItemsTab q={q} detailItems={detailItems} setDetailItems={setDetailItems}/>
      )}

      {tab === 'adj' && (
        <div className="p-6 grid grid-cols-2 gap-3">
          {(() => {
            const visibleAdj = detailAttachments;
            if (visibleAdj.length === 0) return (
              <div className="col-span-2 text-[13px] text-ink-400 py-6 text-center">Sin adjuntos</div>
            );
            return visibleAdj.map(a => {
              const ext = extOf(a.filename, a.mimeType);
              const isPdf = ext === 'pdf';
              // Muchos clientes mandan lo que quieren cotizar como una captura
              // pegada en el mail: conviene verla, no solo poder bajarla.
              const isImg = (a.mimeType || '').startsWith('image/');
              const fileUrl = authUrl(`/uploads/attachments/${a.filename}`);
              const displayName = a.originalName || a.filename.replace(/^[0-9a-f-]{36}-(\d{13}-)?/i, '');
              return (
                <div key={a.id} className="bg-white border border-line rounded-xl p-3 flex items-center gap-3">
                  {isImg ? (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                       className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-line bg-surface block"
                       title="Ver en grande">
                      <img src={fileUrl} alt={displayName} className="w-full h-full object-cover"/>
                    </a>
                  ) : (
                    <div className={cx('w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-[11px] shrink-0', extBg(ext))}>
                      {ext.toUpperCase().slice(0,4)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-ink-900 truncate">{displayName}</div>
                    <div className="text-[11px] text-ink-500">
                      {fmtBytes(a.size)}{a.size ? ' · ' : ''}{fmtDate(a.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isPdf && (
                      <button
                        onClick={() => setPdfPreview({ url: fileUrl, filename: displayName })}
                        className="h-8 px-2.5 rounded-lg hover:bg-surface text-ink-500 text-[12px] font-medium flex items-center gap-1"
                        title="Ver PDF">
                        <Icon name="eye" size={13}/>Ver
                      </button>
                    )}
                    {isImg && (
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                        className="h-8 px-2.5 rounded-lg hover:bg-surface text-ink-500 text-[12px] font-medium flex items-center gap-1"
                        title="Ver la imagen en grande">
                        <Icon name="eye" size={13}/>Ver
                      </a>
                    )}
                    <a href={fileUrl} download={displayName}
                      className="w-8 h-8 rounded-lg hover:bg-surface text-ink-500 flex items-center justify-center"
                      title="Descargar">
                      <Icon name="download" size={14}/>
                    </a>
                    <button
                      onClick={() => handleDeleteAttachment(a.id)}
                      className="w-8 h-8 rounded-lg hover:bg-red-50 text-ink-400 hover:text-red-500 flex items-center justify-center"
                      title="Eliminar adjunto">
                      <Icon name="trash-2" size={13}/>
                    </button>
                  </div>
                </div>
              );
            });
          })()}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="col-span-2 py-6 border-2 border-dashed border-line rounded-xl text-ink-500 hover:bg-surface disabled:opacity-50">
            {uploading
              ? <><Icon name="loader" size={14} className="inline mr-1 animate-spin"/> Subiendo…</>
              : <><Icon name="plus" size={14} className="inline mr-1"/> Subir adjunto</>}
          </button>
        </div>
      )}

      {/* Modal preview PDF */}
      {pdfPreview && (() => {
        const pdfDisplayName = pdfPreview.filename || '';
        return (
          <div className="fixed inset-0 z-[200] flex flex-col bg-black/80" onClick={() => setPdfPreview(null)}>
            <div className="flex items-center justify-between px-5 py-3 shrink-0" style={{background:'#004669'}} onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded bg-red-500 flex items-center justify-center text-white text-[10px] font-bold">PDF</div>
                <span className="text-white text-sm font-medium truncate max-w-[500px]">{pdfDisplayName}</span>
              </div>
              <div className="flex items-center gap-2">
                <a href={pdfPreview.url} download={pdfDisplayName}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-white text-[12px] font-medium hover:bg-white/10">
                  <Icon name="download" size={13}/>Descargar
                </a>
                <button onClick={() => setPdfPreview(null)}
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white">
                  <Icon name="x" size={16}/>
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              <iframe
                src={pdfPreview.url}
                className="w-full h-full rounded-lg bg-white"
                title={pdfDisplayName}
              />
            </div>
          </div>
        );
      })()}

      {tab === 'historial' && (
        <div className="p-6">
          {history.length === 0 ? (
            <div className="text-[13px] text-ink-400 py-8 text-center">Sin actividad registrada</div>
          ) : (
            <div className="space-y-0">
              {history.map((a, i) => {
                const iconMap = {
                  CREATED:             { name:'plus-circle',    cls:'text-emerald-600 bg-emerald-50' },
                  STAGE_CHANGE:        { name:'arrow-right',    cls:'text-brand bg-brandSoft' },
                  NOTE_ADDED:          { name:'message-square', cls:'text-ink-500 bg-surface' },
                  ASSIGNED:            { name:'user',           cls:'text-orange-600 bg-orange-50' },
                  LINKED:              { name:'link',           cls:'text-violet-600 bg-violet-50' },
                  ATTACHMENT_UPLOADED: { name:'paperclip',      cls:'text-sky-600 bg-sky-50' },
                  REMINDER_SENT:       { name:'bell',           cls:'text-amber-600 bg-amber-50' },
                  PEDIDO_EDITED:       { name:'pencil',         cls:'text-ink-500 bg-surface' },
                  DUPLICATE_MERGED:    { name:'copy',           cls:'text-ink-500 bg-surface' },
                  PRIORITY_CHANGED:    { name:'activity',       cls:'text-amber-600 bg-amber-50' },
                  REVISION_CREADA:     { name:'copy',           cls:'text-brand bg-brandSoft' },
                  ANULADA:             { name:'archive',        cls:'text-ink-500 bg-surface' },
                  REACTIVADA:          { name:'rotate-ccw',     cls:'text-emerald-600 bg-emerald-50' },
                };
                const ic = iconMap[a.action] || { name:'activity', cls:'text-ink-500 bg-surface' };
                const isLast = i === history.length - 1;
                // ¿Este evento viene de la quote vinculada?
                const isFromLinked = a._fromCode && a._fromCode !== q.code;
                const linkedTypeTone = a._fromType === 'SOLICITUD' ? 'sky' : a._fromType === 'PRESUPUESTO' ? 'blue' : a._fromType === 'NOTA_PEDIDO' ? 'purple' : a._fromType === 'ORDER' ? 'amber' : 'gray';
                return (
                  <div key={`${a.id||i}-${a._fromCode||''}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={cx('w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                        isFromLinked ? 'ring-2 ring-offset-1 ring-violet-200 ' + ic.cls : ic.cls)}>
                        <Icon name={ic.name} size={14}/>
                      </div>
                      {!isLast && <div className="w-px flex-1 bg-line mt-1 mb-1"/>}
                    </div>
                    <div className={cx('flex-1', isLast ? 'pb-0' : 'pb-4')}>
                      <div className={cx('border rounded-xl px-4 py-3', isFromLinked ? 'bg-violet-50/40 border-violet-100' : 'bg-white border-line')}>
                        {isFromLinked && (
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Icon name="link" size={11} className="text-violet-400"/>
                            <span className="text-[10.5px] font-semibold text-violet-500 mono">{a._fromCode}</span>
                            <Badge tone={linkedTypeTone} className="text-[9px]">{a._fromType}</Badge>
                          </div>
                        )}
                        <p className="text-[13px] text-ink-900 leading-snug">{a.detail}</p>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-ink-500">
                          <span className="font-medium">{a.user?.name || 'Sistema'}</span>
                          <span>·</span>
                          <span className="mono">{fmtDateTime(a.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'notas' && (
        <div className="p-6 space-y-3">
          {notesLoading ? (
            <div className="text-[13px] text-ink-400 py-4 text-center">Cargando notas…</div>
          ) : notes.map((n, i) => (
            <div key={n.id || i} className="bg-white border border-line rounded-xl p-3 flex gap-3">
              <Avatar name={n.user?.name || '?'} size={32}/>
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-[13px] text-ink-900">{n.user?.name}</span>
                  <span className="text-[11px] text-ink-500 mono">{fmtDateTime(n.createdAt)}</span>
                </div>
                <p className="text-[13px] text-ink-700 mt-1 leading-snug">{n.text}</p>
              </div>
            </div>
          ))}
          <div className="bg-white border border-line rounded-xl p-3">
            <textarea ref={noteInputRef} rows="3" value={newNote} onChange={e=>setNewNote(e.target.value)}
              className="w-full outline-none text-[13px] placeholder:text-ink-400 resize-none" placeholder="Escribir una nota para el equipo…"/>
            {mentionedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1.5">
                {mentionedUsers.map(u => (
                  <span key={u.id} className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded bg-brandSoft text-brand text-[11px] font-medium">
                    @{u.name.split(' ')[0]}
                    <button onClick={()=>setMentionedUsers(ms=>ms.filter(x=>x.id!==u.id))} className="hover:opacity-60"><Icon name="x" size={10}/></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-line">
              <div className="flex gap-1.5 text-ink-500">
                <button className="w-7 h-7 rounded hover:bg-surface"><Icon name="paperclip" size={13}/></button>
                <div className="relative">
                  <button className="w-7 h-7 rounded hover:bg-surface" onClick={()=>setMentionPickerOpen(o=>!o)}><Icon name="at-sign" size={13}/></button>
                  {mentionPickerOpen && (
                    <MentionPicker users={users} excludeId={currentUserId}
                      onPick={(u)=>{
                        setMentionedUsers(ms => ms.some(x=>x.id===u.id) ? ms : [...ms, u]);
                        setNewNote(t => (t && !/\s$/.test(t) ? t+' ' : t) + `@${u.name} `);
                        setMentionPickerOpen(false);
                      }}
                      onClose={()=>setMentionPickerOpen(false)}/>
                  )}
                </div>
              </div>
              <button className="btn-primary text-xs py-1.5" onClick={handleAddNote}
                disabled={savingNote || !newNote.trim()}
                style={savingNote || !newNote.trim() ? {opacity:.45,cursor:'not-allowed'} : {}}>
                {savingNote ? 'Guardando…' : 'Publicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
    {rejectPending && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-ink-900/40" onClick={()=>setRejectPending(false)}/>
        <div className="relative bg-white rounded-xl shadow-pop p-5 w-[380px]">
          <div className="text-sm font-semibold mb-3">Motivo de rechazo</div>
          <select value={rejectReason} onChange={e=>setRejectReason(e.target.value)} className="inp w-full mb-3">
            <option value="">Seleccionar motivo…</option>
            {REJECT_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <textarea rows="3" value={rejectNotes} onChange={e=>setRejectNotes(e.target.value)}
            className="inp w-full resize-none mb-3" placeholder="Observaciones opcionales…"/>
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={()=>setRejectPending(false)}>Cancelar</button>
            <button className="btn-ghost text-bad border-red-200 hover:bg-red-50"
              disabled={!rejectReason} onClick={submitReject}
              style={!rejectReason?{opacity:.45,cursor:'not-allowed'}:{}}>
              Marcar rechazada
            </button>
          </div>
        </div>
      </div>
    )}
    {emailModalOpen && (
      <SendEmailModal
        quote={{
          ...q,
          clientName:  cli?.name  || q.clientName || '',
          clientEmail: cli?.email || '',
          sellerName:  sel?.name  || '',
          flexxus:     q.flexxus  || '',
        }}
        attachments={detailAttachments}
        isSolicitud={isSolicitud}
        onClose={() => setEmailModalOpen(false)}
        onSent={async () => {
          // Refrescar quote para actualizar etapa si avanzó
          try {
            const fresh = await CrmApi.getQuotes();
            setQuotes(fresh);
          } catch (_) {}
        }}
      />
    )}
    {reminderOpen && (() => {
      const daysSent = Math.floor((Date.now() - new Date(q.createdAt).getTime()) / 86400000);
      const defSubject = reminderSubject || `Seguimiento presupuesto ${q.flexxus || q.code} — MySelec`;
      const defBody = reminderBody || `Hola ${cli?.name || ''},\n\nTe escribimos para hacer seguimiento del presupuesto ${q.flexxus || q.code} que te enviamos hace ${daysSent} días.\n\n¿Pudiste revisarlo? Quedamos a disposición para cualquier consulta.\n\nSaludos cordiales,\nEquipo MySelec`;
      const buildReminderGmailUrl = () => {
        const params = new URLSearchParams();
        if (cli?.email) params.set('to', cli.email);
        params.set('su', reminderSubject || defSubject);
        params.set('body', reminderBody || defBody);
        params.set('view', 'cm'); params.set('fs', '1');
        return 'https://mail.google.com/mail/?' + params.toString();
      };
      return (
        <>
          <div className="fixed inset-0 z-50 modal-overlay" onClick={() => setReminderOpen(false)}/>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setReminderOpen(false)}>
            <div className="bg-white rounded-2xl shadow-pop w-full max-w-lg border border-line modal-enter" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-line flex items-center justify-between">
                <div>
                  <div className="font-semibold text-ink-900 text-[14px]">Enviar recordatorio</div>
                  <div className="text-[11.5px] text-ink-400 mt-0.5">
                    {q.code} · {cli?.name || 'Sin cliente'} · {daysSent}d sin respuesta
                  </div>
                </div>
                <button onClick={() => setReminderOpen(false)} className="btn-ghost p-1"><Icon name="x" size={16}/></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-ink-500 mb-1">Para</label>
                  <input className="inp w-full bg-surface text-ink-500 cursor-not-allowed text-[13px]" value={cli?.email || ''} readOnly/>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-ink-500 mb-1">Asunto</label>
                  <input className="inp w-full text-[13px]" value={reminderSubject || defSubject}
                    onChange={e => setReminderSubject(e.target.value)}/>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-ink-500 mb-1">Mensaje</label>
                  <textarea className="inp w-full text-[13px] min-h-[160px] leading-relaxed"
                    value={reminderBody || defBody}
                    onChange={e => setReminderBody(e.target.value)}/>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-700 space-y-1">
                  <div>Se envía desde <strong>iamyselec@gmail.com</strong>. Para enviar desde tu Gmail personal usá "Abrir en Gmail".</div>
                  <div>Al enviar, se registrará como actividad y se reprogramará el seguimiento automáticamente.</div>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-line flex items-center justify-between">
                <button onClick={() => setReminderOpen(false)} className="btn-ghost" disabled={reminderSending}>Cancelar</button>
                <div className="flex items-center gap-2">
                  <button className="btn-primary" disabled={reminderSending}
                    onClick={async () => {
                      setReminderSending(true);
                      try {
                          await CrmApi.sendReminder(q.id, {
                          subject: reminderSubject || defSubject,
                          body: reminderBody || defBody,
                        });
                        pushToast('Recordatorio enviado a ' + (cli?.email || ''), 'ok');
                        setReminderOpen(false);
                        const fresh = await CrmApi.getQuotes();
                        setQuotes(fresh);
                      } catch (err) {
                        pushToast(err.message || 'Error al enviar', 'bad');
                      } finally {
                        setReminderSending(false);
                      }
                    }}>
                    <Icon name="send" size={13}/>{reminderSending ? 'Enviando...' : 'Enviar recordatorio'}
                  </button>
                  <button className="btn-ghost border border-line" onClick={() => { window.open(buildReminderGmailUrl(), '_blank'); }}>
                    <Icon name="external-link" size={13}/>Abrir en Gmail
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    })()}
    </>
  );
}

Object.assign(window, { QuoteDetail, Field, TabBar });
